/**
 * @fileoverview api.ts - Core API definitions and legacy collection checking
 *
 * This file contains:
 * - NFT Collection definitions and data (COLLECTIONS array)
 * - Interface definitions for API responses
 * - Legacy collection checking functions
 * - API constants and configuration
 * - Direct blockchain interaction utilities
 *
 * For optimized parallel collection checking across multiple blockchains,
 * see optimized-api.ts which uses this file's data and interfaces.
 */

import { ethers } from "ethers"
import { fetchRoninChainNFTs } from "@/lib/ronin-chain"
import { withTimeout } from "@/lib/protection"
import { fetchBaseChainNFTs as fetchBaseChainNFTsUtil } from "./base-chain"
import { mainnetProvider } from "./ethereum-config"
import { checkCollectionsWithDelegatedWallets } from "./optimized-api"

// Add this new interface near the top of the file, after the NFTCollection interface:

// Interface for token ID range to identify collections on shared contracts
interface TokenIdRange {
  start: string
  end: string
}

// Update the NFTCollection interface to include the new fields:
export interface NFTCollection {
  id: string
  name: string
  image: string
  cubeImage: string
  contractAddress: string
  relatedContracts?: string[] // Add this line
  baseContracts?: string[] // Add this line for Base chain contracts
  roninContracts?: string[] // Add this line for Ronin chain contracts
  floorPrice?: number
  tokenCount?: number
  owned: boolean
  chain?: "ethereum" | "base" | "ronin" // Update to include ronin
  hidden?: boolean // Add hidden property
  openSeaSlug?: string // Add OpenSea slug for shared contracts
  tokenIdRange?: TokenIdRange // Add token ID range for shared contracts
  addedAt?: number // Timestamp when the collection was added
  popularity?: number // Add popularity score for fallback
}

// Update the COLLECTIONS array with new collections
export const COLLECTIONS: NFTCollection[] = [
  // Update the BAYC collection image
  {
    id: "bayc",
    name: "Bored Ape Yacht Club",
    image:
      "https://i.seadn.io/gae/Ju9CkWtV-1Okvf45wo8UctR-M9He2PjILP0oOvxE89AyiPPGtrR3gysu1Zgy0hjd2xKIgjJJtWIc0ybj4Vd7wv8t3pxDGHoJBzDB?w=500&auto=format",
    cubeImage: "/images/bayc-cube.webp",
    contractAddress: "0xBC4CA0EdA7647A8aB7C2061c2E118A18a936f13D",
    chain: "ethereum",
    relatedContracts: [
      "0xba30E5F9Bb24caa003E9f2f0497Ad287FDF95623", // Bored Ape Kennel Club
    ],
    floorPrice: 68.49,
    tokenCount: 0,
    owned: false,
    addedAt: 1744156800000, // April 30, 2021
    popularity: 9,
  },
  // Update the MAYC collection
  {
    id: "mayc",
    name: "Mutant Ape Yacht Club",
    image: "/images/mayc-avatar.png",
    cubeImage: "/images/mayc-cube.webp",
    contractAddress: "0x60E4d786628Fea6478F785A6d7e704777c86a7c6",
    chain: "ethereum",
    openSeaSlug: "mutant-ape-yacht-club",
    floorPrice: 14.8,
    tokenCount: 0,
    owned: false,
    addedAt: 1629504000000, // August 21, 2021
    popularity: 8,
  },
  {
    id: "coolcats",
    name: "Cool Cats",
    image:
      "https://i.seadn.io/gae/LIov33kogXOK4XZd2ESj29sqm_Hww5JSdO7AFn5wjt8xgnJJ0UpNV9yITqxra3s_LMEW1AnnrgOVB_hDpjJRA1uF4skI5Sdi_9rULi8?w=500&auto=format",
    cubeImage: "/images/coolcats-cube.webp",
    contractAddress: "0x1A92f7381B9F03921564a437210bB9396471050C",
    chain: "ethereum",
    relatedContracts: [
      "0x86c10d10eca1fca9daf87a279abccabe0063f247", // CoolCats related
    ],
    floorPrice: 14.2,
    tokenCount: 0,
    owned: false,
    addedAt: 1613779200000, // February 20, 2021
    popularity: 8,
  },
  {
    id: "deadfellaz",
    name: "Deadfellaz",
    image: "/images/deadfellaz-avatar.png",
    cubeImage: "/images/deadfellaz-cube.webp",
    contractAddress: "0x2acAb3DEa77832C09420663b0E1cB386031bA17B",
    chain: "ethereum",
    floorPrice: 1.3,
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744156800000,
  },
  // Update the Azuki collection image
  {
    id: "azuki",
    name: "Azuki",
    image:
      "https://i.seadn.io/gae/H8jOCJuQokNqGBpkBN5wk1oZwO7LM8bNnrHCaekV2nKjnCqw6UB5oaH8XyNeBDj6bA_n1mjejzhFQUP3O1NfjFLHr3FOaeHcTOOT?w=500&auto=format",
    cubeImage: "/images/azuki-cube.webp",
    contractAddress: "0xED5AF388653567Af2F388E6224dC7C4b3241C544",
    chain: "ethereum",
    openSeaSlug: "azuki",
    floorPrice: 14.2,
    tokenCount: 0,
    owned: false,
    addedAt: 1744156800000,
  },
  // Find the Doodles collection entry in the COLLECTIONS array and update the cubeImage property
  // Around line 100-120

  {
    id: "doodles",
    name: "Doodles",
    image:
      "https://i.seadn.io/gae/7B0qai02OdHA8P_EOVK672qUliyjQdQDGNrACxs7WnTgZAkJa_wWURnIFKeOh5VTf8cfTqW3wQpozGedaC9mteKphEOtztls02RlWQ?w=500&auto=format",
    cubeImage: "/images/doodles-cube.webp", // This path remains the same, but the file is updated
    contractAddress: "0x8a90CAb2b38dba80c64b7734e58Ee1dB38B8992e",
    chain: "ethereum",
    relatedContracts: [
      "0x620b70123fB810F6C653DA7644b5dD0b6312e4D8", // Related contract
    ],
    floorPrice: 2.69,
    tokenCount: 0,
    owned: false,
    addedAt: 1744156800000,
  },
  // Update the Pudgy Penguins collection
  {
    id: "pudgy",
    name: "Pudgy Penguins",
    image: "/images/pudgy-penguins-avatar.png",
    cubeImage: "/images/pudgy-cube.webp",
    contractAddress: "0xBd3531dA5CF5857e7CfAA92426877b022e612cf8",
    chain: "ethereum",
    floorPrice: 5.87,
    tokenCount: 0,
    owned: false,
    popularity: 7,
    addedAt: 1744156800000,
  },
  {
    id: "wonderpals",
    name: "Wonderpals",
    image: "/images/wonderpals-avatar.png",
    cubeImage: "/images/wonderpals-cube.webp",
    contractAddress: "0x3Acce66cD37518A6d77d9ea3039E00B3A2955460", // Updated to correct address
    chain: "ethereum",
    floorPrice: 0.24,
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744156800000,
  },
  // Update the Nyan Cat collection
  {
    id: "nyan",
    name: "Nyan Cat",
    image: "/images/nyan-cat-avatar.webp",
    cubeImage: "/images/nyan-cube.webp",
    contractAddress: "0xb32979486938aa9694bfc898f35dbed459f44424",
    chain: "ethereum",
    relatedContracts: [
      "0xe7d4de14e1e5bbc50be8b0905a056bec56be7b66", // Related contract
    ],
    floorPrice: 3.2,
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744156800000,
  },
  {
    id: "ilya",
    name: "Ilya Kazakov",
    image: "/images/ilya-kazakov.png",
    cubeImage: "/images/ilya-cube.webp",
    contractAddress: "0x9c4C633600480Ac51433d0632EC62665b5Fc6960",
    chain: "ethereum",
    relatedContracts: [
      "0x9A291DD80cD601f4c02da3f595d78E4B640e8b56", // Ilya Kazakov related
      "0xa3fb1a28817c1c8ada52ac504dc320c40db782b9", // Ilya Kazakov on Base chainX
    ],
    baseContracts: ["0xa3fb1a28817c1c8ada52ac504dc320c40db782b9"], // Explicitly mark Base chain contracts
    floorPrice: 0.75,
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744156800000,
  },
  {
    id: "deekay",
    name: "Deekay",
    image: "/images/deekay-avatar.webp",
    cubeImage: "/images/deekay-cube.webp",
    contractAddress: "0x0825f050e9b021a0e9de8cb1fb10b6c9f41e834c",
    chain: "ethereum",
    relatedContracts: [
      "0x60eac2cc7886942b8da0d577f39232bbd5c117d7", // Deekay related
    ],
    floorPrice: 1.2,
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744156800000,
  },
  // Add the new collections
  {
    id: "chimpers",
    name: "Chimpers",
    image: "/images/chimpers-avatar.png",
    cubeImage: "/images/chimpers-cube.webp",
    contractAddress: "0x80336ad7a747236ef41f47ed2c7641828a480baa",
    chain: "ethereum",
    floorPrice: 0.95, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744243200000,
  },
  {
    id: "vinnie",
    name: "Vinnie Hager",
    image: "/images/vinnie-hager-avatar.png",
    cubeImage: "/images/vinnie-cube.webp",
    contractAddress: "0x614917f589593189ac27ac8b81064cbe450c35e3",
    chain: "ethereum",
    floorPrice: 0.65, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Add Forgotten Runes collection
  {
    id: "forgotten-runes",
    name: "Forgotten Runes",
    image: "/images/forgotten-runes-avatar.png",
    cubeImage: "/images/forgotten-runes-cube.webp",
    contractAddress: "0x521f9c7505005cfa19a8e5786a9c3c9c9f5e6f42", // Main contract
    chain: "ethereum",
    relatedContracts: [
      "0x7c104b4db94494688027cced1e2ebfb89642c80f", // Related contract
      "0x9690b63eb85467be5267a3603f770589ab12dc95", // Related contract
      "0x251b5f14a825c537ff788604ea1b58e49b70726f", // Related contract
    ],
    floorPrice: 1.8, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744243200000,
  },
  // Update the A.N.I.M.O. collection image
  {
    id: "animo",
    name: "A.N.I.M.O.",
    image: "/images/animo-avatar.png",
    cubeImage: "/images/animo-cube.webp",
    contractAddress: "0x569f26145f0c2fcc3da68395be3639009c53b6d8",
    chain: "ethereum",
    floorPrice: 0.9, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744243200000,
  },
  // Add Chonks Society collection
  {
    id: "chonks",
    name: "Chonks Society",
    image: "/images/chonks-society-avatar.png",
    cubeImage: "/images/chonks-cube.webp",
    contractAddress: "0xe68d1aeee2c17e43a955103dab5e341ee439f55c",
    chain: "ethereum",
    floorPrice: 0.8, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Add Renga collection
  {
    id: "renga",
    name: "Renga",
    image: "/images/renga-avatar.webp",
    cubeImage: "/images/renga-cube.webp",
    contractAddress: "0x394e3d3044fc89fcdd966d3cb35ac0b32b0cda91",
    chain: "ethereum",
    floorPrice: 1.5, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744243200000,
  },
  // Add CryptoPunks collection
  {
    id: "cryptopunks",
    name: "CryptoPunks",
    image: "/images/cryptopunks-avatar.png",
    cubeImage: "/images/cryptopunk-cube.webp",
    contractAddress: "0xb47e3cd837ddf8e4c57f05d70ab865de6e193bbb",
    chain: "ethereum",
    floorPrice: 43.0, // Updated to more accurate initial floor price
    tokenCount: 0,
    owned: false,
    popularity: 10,
    addedAt: 1744243200000,
  },
  // Update the Cel Mates collection image
  {
    id: "celmates",
    name: "Cel Mates",
    image: "/images/celmates-avatar.png",
    cubeImage: "/images/celmates-cube.webp",
    contractAddress: "0x23f0dcecfe50f70d0c87578fd6a14f59900ab5e1",
    chain: "ethereum",
    floorPrice: 0.7, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Add Garbage Friends collection
  {
    id: "garbage-friends",
    name: "Garbage Friends",
    image: "/images/garbage-friends-avatar.webp",
    cubeImage: "/images/garbage-friends-cube.webp",
    contractAddress: "0xef371a11e6adb9dc3d333dafcb1ff5afbcb6cdc8",
    chain: "ethereum",
    floorPrice: 0.65, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Update the a KID called BEAST collection image
  {
    id: "a-kid-called-beast",
    name: "a KID called BEAST",
    image: "/images/a-kid-called-beast-avatar.webp",
    cubeImage: "/images/akcb-cube.webp",
    contractAddress: "0x77372a4cc66063575b05b44481f059be356964a4", // Same as Garbage Friends
    chain: "ethereum",
    floorPrice: 0.75, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Add Kaiju Kings collection
  {
    id: "kaiju-kings",
    name: "Kaiju Kings",
    image: "/images/kaijukings-avatar.webp",
    cubeImage: "/images/kaijukings-cube.webp",
    contractAddress: "0x0c2e57efddba8c768147d1fdf9176a0a6ebd5d83",
    chain: "ethereum",
    floorPrice: 0.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Update the 1337 Skulls collection image
  {
    id: "1337-skulls",
    name: "1337 Skulls",
    image: "/images/1337-avatar.webp",
    cubeImage: "/images/1337-cube.webp",
    contractAddress: "0x9251dec8df720c2adf3b6f46d968107cbbadf4d4",
    chain: "ethereum",
    floorPrice: 0.69, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744243200000,
  },
  // Update the Kitaro World collection
  {
    id: "kitaro-world",
    name: "Kitaro World",
    image: "/images/kitaro-world-avatar.webp",
    cubeImage: "/images/kitaro-world-cube.webp",
    contractAddress: "0x39d3230c84efcb5559d22f499151934476b5c16d",
    chain: "ethereum",
    floorPrice: 0.55,
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1744243200000,
  },
  // Add CryptoMories collection
  {
    id: "cryptomories",
    name: "CryptoMories",
    image: "/images/cryptomories-avatar.webp",
    cubeImage: "/images/cryptomories-cube.webp",
    contractAddress: "0x361a5881f18b968422e9258cb7c1fa53ff4cdd38",
    chain: "ethereum",
    floorPrice: 0.42, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1744243200000,
  },
  // Add CryptoFoxes collection
  {
    id: "cryptofoxes",
    name: "CryptoFoxes",
    image: "/images/cryptofoxes-avatar.png",
    cubeImage: "/images/cryptofoxes-cube.webp",
    contractAddress: "0xa9fdb3f96fae7c12d70393659867c6115683ada0",
    chain: "ethereum",
    floorPrice: 0.38, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1744243200000,
  },
  // Add the Opepen Edition collection to the COLLECTIONS array after the CryptoFoxes collection
  // (around line 380, before the closing bracket of the COLLECTIONS array)

  // Update the Opepen Edition collection
  {
    id: "opepen",
    name: "Opepen Edition",
    image: "/images/opepen-avatar.webp",
    cubeImage: "/images/opepen-cube.webp",
    contractAddress: "0x6339e5e072086621540d0362c4e3cea0d643e114",
    chain: "ethereum",
    floorPrice: 0.45,
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1744924800000,
  },

  // Update the BEANZ Official collection image
  {
    id: "beanz",
    name: "BEANZ Official",
    image: "/images/beanz-avatar.webp",
    cubeImage: "/images/beanz-cube.webp",
    contractAddress: "0x306b1ea3ecdf94ab739f1910bbda052ed4a9f949",
    chain: "ethereum",
    floorPrice: 1.25, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744924800000,
  },
  // Add the new collections to the COLLECTIONS array after the beanz collection (around line 380)

  // Update the 0N1 Force collection image
  {
    id: "0n1force",
    name: "0N1 Force",
    image: "/images/0n1force-avatar.webp",
    cubeImage: "/images/0n1force-cube.webp",
    contractAddress: "0x3bf2922f4520a8ba0c2efc3d2a1539678dad5e9d",
    chain: "ethereum",
    floorPrice: 0.95, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744924800000,
  },

  // Add CyberKongz collection
  {
    id: "cyberkongz",
    name: "CyberKongz",
    image: "/images/cyberkongz-avatar.webp",
    cubeImage: "/images/cyberkongz-cube.webp",
    contractAddress: "0x57a204aa1042f6e66dd7730813f4024114d74f37",
    chain: "ethereum",
    floorPrice: 1.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744924800000,
  },

  // Add Chum Chums NFT collection
  {
    id: "chumchums",
    name: "Chum Chums NFT",
    image: "/images/chumchums-avatar.webp",
    cubeImage: "/images/chumchums-cube.webp",
    contractAddress: "0xe987e9b07ca431fe0c7e8f431fa4f94ab9ca2423",
    chain: "ethereum",
    floorPrice: 0.65, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744924800000,
  },

  // Add Degen Toonz collection
  {
    id: "degentoonz",
    name: "Degen Toonz",
    image: "/images/degentoonz-avatar.webp",
    cubeImage: "/images/degentoonz-cube.webp",
    contractAddress: "0x4d7d2e237d64d1484660b55c0a4cc092fa5e6716",
    chain: "ethereum",
    floorPrice: 0.78, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744924800000,
  },
  // Add Good Vibes Club collection
  {
    id: "goodvibes",
    name: "Good Vibes Club",
    image: "/images/goodvibes-avatar.webp",
    cubeImage: "/images/goodvibes-cube.webp",
    contractAddress: "0xb8ea78fcacef50d41375e44e6814ebba36bb33c4",
    chain: "ethereum",
    floorPrice: 0.58, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1744924800000,
  },

  // Add Fang Gang collection
  {
    id: "fanggang",
    name: "Fang Gang",
    image: "/images/fanggang-avatar.webp",
    cubeImage: "/images/fanggang-cube.webp",
    contractAddress: "0x9d418c2cae665d877f909a725402ebd3a0742844",
    chain: "ethereum",
    floorPrice: 0.72, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744924800000,
  },
  // Add the new collections to the COLLECTIONS array after the Fang Gang collection (around line 480)

  // Add Franky's Dinner collection
  {
    id: "frankysdinner",
    name: "Franky's Dinner",
    image: "/images/frankysdinner-avatar.webp",
    cubeImage: "/images/frankysdinner-cube.webp",
    contractAddress: "0x8c3ee12a9984c4d2a2a74034abd9cb1685d13fc4",
    chain: "ethereum",
    floorPrice: 0.68, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744924800000,
  },

  // Add Space Riders collection
  {
    id: "spaceriders",
    name: "Space Riders",
    image: "/images/spaceriders-avatar.webp",
    cubeImage: "/images/spaceriders-cube.webp",
    contractAddress: "0xc9d198089d6c31d0ca5cc5b92c97a57a97bbfde2",
    chain: "ethereum",
    floorPrice: 0.75, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744924800000,
  },
  // Add the new collections to the COLLECTIONS array after the Space Riders collection (around line 530)

  // Add Woodies collection
  {
    id: "woodies",
    name: "Woodies",
    image: "/images/woodies-avatar.webp",
    cubeImage: "/images/woodies-cube.webp",
    contractAddress: "0x134460d32fc66a6d84487c20dcd9fdcf92316017",
    chain: "ethereum",
    floorPrice: 0.82, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744924800000,
  },

  // Add Gutter Cat Gang collection
  {
    id: "gutter-cat-gang",
    name: "Gutter Cat Gang",
    image: "/images/gutter-cat-gang-avatar.webp",
    cubeImage: "/images/gutter-cat-gang-cube.webp",
    contractAddress: "0xedb61f74b0d09b2558f1eeb79b247c1f363ae452",
    chain: "ethereum",
    floorPrice: 1.45, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744924800000,
  },
  // Update the Axie Infinity collection image
  {
    id: "axie",
    name: "Axie Infinity",
    image: "/images/axie-avatar.webp",
    cubeImage: "/images/axie-cube.webp",
    contractAddress: "0x32950db2a7164ae833121501c797d79e7b79d74c",
    chain: "ronin",
    roninContracts: ["0x32950db2a7164ae833121501c797d79e7b79d74c"], // Add roninContracts for Ronin chain support
    floorPrice: 0.00056, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 7,
    addedAt: 1744156800000,
  },
  // Add the new collections to the COLLECTIONS array at the end (around line 580)

  // Update the Nakamigos collection
  {
    id: "nakamigos",
    name: "Nakamigos",
    image: "/images/nakamigos-avatar.webp",
    cubeImage: "/images/nakamigos-cube.webp",
    contractAddress: "0xd774557b647330c91bf44cfeab205095f7e6c367",
    chain: "ethereum",
    floorPrice: 1.35,
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1745184000000,
  },

  // Update the Alien Frens collection image
  {
    id: "alienfrens",
    name: "Alien Frens",
    image: "/images/alienfrens-avatar.webp",
    cubeImage: "/images/alienfrens-cube.webp",
    contractAddress: "0x123b30e25973fecd8354dd5f41cc45a3065ef88c",
    chain: "ethereum",
    floorPrice: 0.92, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1745184000000,
  },

  // Add Cryptoon Goonz collection
  {
    id: "cryptoongoonz",
    name: "Cryptoon Goonz",
    image: "/images/cryptoongoonz-avatar.webp",
    cubeImage: "/images/cryptoongoonz-cube.webp",
    contractAddress: "0x0322f6f11a94cfb1b5b6e95e059d8deb2bf17d6a",
    relatedContracts: [
      "0xef87529ca9c67a849bd7013837fc42d4de92ca82", // Related contract
    ],
    chain: "ethereum",
    openSeaSlug: "badbad",
    floorPrice: 0.88, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1745184000000,
  },

  // Update the Mars Cats Voyage collection
  {
    id: "marscatsvoyage",
    name: "Mars Cats Voyage",
    image: "/images/marscatsvoyage-avatar.webp",
    cubeImage: "/images/marscatsvoyage-cube.webp",
    contractAddress: "0xdd467a6c8ae2b39825a452e06b4fa82f73d4253d",
    chain: "ethereum",
    floorPrice: 0.65,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745184000000,
  },
  // Add the new collections to the COLLECTIONS array at the end (after Mars Cats Voyage)

  // Update the Meebits collection
  {
    id: "meebits",
    name: "Meebits",
    image: "/images/meebits-avatar.webp",
    cubeImage: "/images/meebits-cube.webp",
    contractAddress: "0x7bd29408f11d2bfc23c34f18275bbf23bb716bc7",
    chain: "ethereum",
    floorPrice: 2.75,
    tokenCount: 0,
    owned: false,
    popularity: 7,
    addedAt: 1745184000000,
  },

  // Update the Rare Apepes collection
  {
    id: "rareapepes",
    name: "Rare Apepes",
    image: "/images/rareapepes-avatar.webp",
    cubeImage: "/images/rareapepes-cube.webp",
    contractAddress: "0x31d45de84fde2fb36575085e05754a4932dd5170",
    chain: "ethereum",
    floorPrice: 0.55,
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1745184000000,
  },

  // Add Sappy Seals collection
  {
    id: "sappyseals",
    name: "Sappy Seals",
    image: "/images/sappyseals-avatar.png",
    cubeImage: "/images/sappyseals-cube.webp",
    contractAddress: "0x364c828ee171616a39897688a831c2499ad972ec",
    chain: "ethereum",
    relatedContracts: [
      "0x1c70d0a86475cc707b48aa79f112857e7957274f", // Additional Sappy Seals contract
    ],
    floorPrice: 1.15, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1745184000000,
  },
  // Let's fix the duplicate CryptoMories entry in the COLLECTIONS array

  // Find the duplicate CryptoMories collection entry (around line 700-720)
  // Remove the second instance of the CryptoMories collection:

  // Remove or comment out this duplicate entry:
  // Update the CryptoMories collection
  // {
  //   id: "cryptomories",
  //   name: "CryptoMories",
  //   image: "/images/cryptomories-avatar.webp",
  //   cubeImage: "/images/cryptomories-cube.webp",
  //   contractAddress: "0x361a5881f18b968422e9258cb7c1fa53ff4cdd38",
  //   chain: "ethereum",
  //   floorPrice: 0.42,
  //   tokenCount: 0,
  //   owned: false,
  // },
  // After the existing collections, add the new Peaceful Groupies collection (just before the closing bracket of the COLLECTIONS array)

  // Add Peaceful Groupies collection
  {
    id: "peaceful-groupies",
    name: "Peaceful Groupies",
    image: "/images/groupies-avatar.webp",
    cubeImage: "/images/groupies-cube.webp",
    contractAddress: "0x4f89cd0cae1e54d98db6a80150a824a533502eea",
    chain: "ethereum",
    floorPrice: 0.62, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745270400000,
  },
  // Add the new collections to the COLLECTIONS array at the end (around line 700-750)

  // Add FTCG collection
  {
    id: "ftcg",
    name: "FTCG - Fake Trading Card Game",
    contractAddress: "0x9a291dd80cd601f4c02da3f595d78e4b640e8b56",
    image: "/images/ftcg-avatar.jpeg",
    cubeImage: "/images/ftcg-cube.webp",
    chain: "base",
    openSeaSlug: "fake-trading-card-game",
    // Also present on Base with the same contract address; include for matching Base results
    baseContracts: ["0x9a291dd80cd601f4c02da3f595d78e4b640e8b56"],
    floorPrice: 1, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744156800000,
  },

  // Add Persona collection
  {
    id: "persona",
    name: "Persona",
    image: "/images/persona-avatar.png",
    cubeImage: "/images/persona-cube.webp",
    contractAddress: "0xbabafdd8045740449a42b788a26e9b3a32f88ac1",
    chain: "ethereum",
    relatedContracts: [
      "0x6502820f3f035c7a9fc0ebd3d74a0383306c5137", // Additional Persona contract
    ],
    floorPrice: 0.78, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744156800000,
  },

  // Add Figuregot collection
  {
    id: "figuregot",
    name: "Figuregot",
    image: "/images/figuregot-avatar.jpeg",
    cubeImage: "/images/figuregot-cube.webp",
    contractAddress: "0x13b5aef8a167c1a8f36dbe9d55e3d8db4ce3f767",
    chain: "ethereum",
    floorPrice: 0.72, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744156800000,
  },

  // Add Doge Pound collection
  {
    id: "doge-pound",
    name: "Doge Pound",
    image: "/images/dogepound-avatar.webp",
    cubeImage: "/images/dogepound-cube.webp",
    contractAddress: "0xf4ee95274741437636e748ddac70818b4ed7d043",
    chain: "ethereum",
    floorPrice: 0.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1744156800000,
  },

  // Add Mocaverse collection
  {
    id: "mocaverse",
    name: "Mocaverse",
    image: "/images/mocaverse-avatar.gif",
    cubeImage: "/images/mocaverse-cube.webp",
    contractAddress: "0x59325733eb952a92e069c87f0a6168b29e80627f",
    chain: "ethereum",
    floorPrice: 1.25, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1744156800000,
  },
  // Add the Based Minis collection at the end of the COLLECTIONS array (after Mocaverse)
  {
    id: "based-minis",
    name: "Based Minis",
    image: "/images/basedminis-avatar.webp",
    cubeImage: "/images/basedminis-cube.webp",
    contractAddress: "0xd683f8f08aca669bf9207d72dceb6bac063c34a2",
    chain: "base",
    baseContracts: ["0xd683f8f08aca669bf9207d72dceb6bac063c34a2"], // Add baseContracts for Base chain support
    floorPrice: 0.03, // Updated estimated floor price
    tokenCount: 0,
    owned: false,
    openSeaSlug: "based-minis", // Correct OpenSea slug
    popularity: 6,
    addedAt: 1744156800000,
  },

  // Add the Invisible Friends collection after the Based Minis entry
  {
    id: "invisible-friends",
    name: "Invisible Friends",
    image: "/images/invisible-avatar.webp",
    cubeImage: "/images/invisible-cube.webp",
    contractAddress: "0x59468516a8259058bad1ca5f8f4bff190d30e066",
    chain: "ethereum",
    floorPrice: 1.35, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1744156800000,
  },
  // Add the new collections to the COLLECTIONS array at the end (around line 750-800)

  // Add HPPRS collection
  {
    id: "hpprs",
    name: "HPPRS",
    image: "/images/hpprs-avatar.webp",
    cubeImage: "/images/hpprs-cube.webp",
    contractAddress: "0xe2609354791bf57e54b3f7f9a26b2dacbed61da1",
    chain: "ethereum",
    floorPrice: 0.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745788800000,
  },

  // Add FoxFam collection
  {
    id: "foxfam",
    name: "FoxFam",
    image: "/images/foxfam-avatar.jpeg",
    cubeImage: "/images/foxfam-cube.webp",
    contractAddress: "0x7cba74d0b16c8e18a9e48d3b7404d7739bb24f23",
    chain: "ethereum",
    floorPrice: 0.72, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745788800000,
  },

  // Add Grillz Gang collection
  {
    id: "grillz-gang",
    name: "Grillz Gang",
    image: "/images/grillz-avatar.jpeg",
    cubeImage: "/images/grillz-cube.webp",
    contractAddress: "0xbd9071b63f25dd199079ed80b3b384d78042956b",
    chain: "ethereum",
    floorPrice: 0.68, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745788800000,
  },

  // Add Neko Darumas collection
  {
    id: "neko-darumas",
    name: "Neko Darumas",
    image: "/images/nekodarumas-avatar.jpeg",
    cubeImage: "/images/nekodarumas-cube.webp",
    contractAddress: "0x329b5d29d8b8049e215740f42e5c14aa8be11618",
    chain: "ethereum",
    floorPrice: 0.75, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745788800000,
  },
  // Add the new collections to the COLLECTIONS array (around line 800-850, after the existing collections)

  // Add The Alien Boy collection
  {
    id: "alien-boy",
    name: "The Alien Boy",
    image: "/images/alienboy-avatar.webp",
    cubeImage: "/images/alienboy-cube.webp",
    contractAddress: "0x4581649af66bccaee81eebae3ddc0511fe4c5312",
    chain: "ethereum",
    openSeaSlug: "thealienboy",
    floorPrice: 0.95, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1745788800000,
  },

  // Add Nouns collection
  {
    id: "nouns",
    name: "Nouns",
    image: "/images/nouns-avatars.webp",
    cubeImage: "/images/nouns-cube.webp",
    contractAddress: "0x9c8ff314c9bc7f6e59a9d9225fb22946427edc03",
    chain: "ethereum",
    floorPrice: 28.5, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 9,
    addedAt: 1745788800000,
  },

  // Add Memeland Potatoz collection
  {
    id: "memeland-potatoz",
    name: "Memeland Potatoz",
    image: "/images/potatoz-avatar.gif",
    cubeImage: "/images/potatoz-cube.webp",
    contractAddress: "0x39ee2c7b3cb80254225884ca001f57118c8f21b6",
    chain: "ethereum",
    floorPrice: 1.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1745788800000,
  },

  // Add The Vogu collection
  {
    id: "vogu",
    name: "The Vogu",
    image: "/images/vogu-avatar.webp",
    cubeImage: "/images/vogu-cube.webp",
    contractAddress: "0x18c7766a10df15df8c971f6e8c1d2bba7c7a410b",
    chain: "ethereum",
    floorPrice: 0.78, // Estimated floor price
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1745788800000,
  },
  // Find the end of the COLLECTIONS array (before the closing bracket)
  // Add the Good Morning Cafe and Clone X collections before the closing bracket of the COLLECTIONS array

  // Add Good Morning Cafe collection
  {
    id: "good-morning-cafe",
    name: "Good Morning Cafe",
    image: "/images/gmcafe-avatar.webp",
    cubeImage: "/images/gmcafe-cube.webp",
    contractAddress: "0xe43d741e21d8bf30545a88c46e4ff5681518ebad",
    chain: "ethereum",
    openSeaSlug: "goodmorningcafe",
    floorPrice: 0.65, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1746964800000, // May 11, 2025
    popularity: 4,
  },

  // Add Clone X collection
  {
    id: "clonex",
    name: "Clone X",
    image: "/images/clonex-avatar.webp",
    cubeImage: "/images/clonex-cube.webp",
    contractAddress: "0x49cf6f5d44e70224e2e23fdcdd2c053f30ada28b",
    chain: "ethereum",
    openSeaSlug: "clonex",
    floorPrice: 2.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1638316800000, // December 1, 2021
    popularity: 8,
  },
  // Update the Transmissions909 collection entry with the correct OpenSea slug
  // Find the Transmissions909 collection entry and update it:

  // Update Transmissions909 collection with proper identification
  {
    id: "transmissions909",
    name: "Transmissions909",
    image: "/images/transmissions909-avatar.webp",
    cubeImage: "/images/transmissions909-cube.webp",
    contractAddress: "0x495f947276749ce646f68ac8c248420045cb7b5e",
    chain: "ethereum",
    relatedContracts: [
      "0xae5236c8f9d0b3c12e01adfd2549c4be1499cc95", // Additional Transmissions909 contract
    ],
    // Try without hyphen first, as that seems to be working for the URL
    openSeaSlug: "transmissions909",
    // Add token ID range or specific token IDs for the shared contract
    tokenIdRange: {
      start: "103303256953621353944417634897515408583907948208000647265999118416051878912000",
      end: "103303256953621353944417634897515408583907948208000647265999118516051878912999",
    },
    floorPrice: 0.23,
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1746643200000,
  },

  // Add Moonbirds collection
  {
    id: "moonbirds",
    name: "Moonbirds",
    image: "/images/moonbirds-avatar.webp",
    cubeImage: "/images/moonbirds-cube.webp",
    contractAddress: "0x23581767a106ae21c074b2276d25e5c3e136a68b",
    chain: "ethereum",
    openSeaSlug: "moonbirds",
    floorPrice: 3.85, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1650067200000, // April 16, 2022
    popularity: 8,
  },

  // Add Tigerbob collection
  {
    id: "tigerbob",
    name: "Tigerbob",
    image: "/images/tigerbob-avatar.webp",
    cubeImage: "/images/tigerbob-cube.webp",
    contractAddress: "0xd497a414bb00803e846b53d07fcb742831b24906",
    chain: "ethereum",
    openSeaSlug: "tigerbobnft-genesis",
    floorPrice: 0.06, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1747612800000, // May 19, 2025
    popularity: 8,
  },

  // Add CrypToadz collection
  {
    id: "cryptoadz",
    name: "CrypToadz",
    image: "/images/cryptoadz-avatar.webp",
    cubeImage: "/images/cryptoadz-cube.webp",
    contractAddress: "0x1cb1a5e65610aeff2551a50f76a87a7d3fb649c6",
    chain: "ethereum",
    openSeaSlug: "cryptoadz-by-gremplin",
    floorPrice: 0.5, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1747612800000, // May 19, 2025
    popularity: 8,
  },

  // Add Mooncats collection
  {
    id: "mooncats",
    name: "Mooncats",
    image: "/images/mooncats-avatar.webp",
    cubeImage: "/images/mooncats-cube.webp",
    contractAddress: "0xc3f733ca98e0dad0386979eb96fb1722a1a05e69",
    chain: "ethereum",
    openSeaSlug: "acclimatedmooncats",
    floorPrice: 0.18, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1747612800000, // May 19, 2025
    popularity: 8,
  },

  // Find the Toy Faces by Amrit Pal collection entry and ensure it's properly configured
  // If it's not properly defined, update it with the following configuration:

  // Update Toy Faces by Amrit Pal collection with proper identification
  {
    id: "toyfaces",
    name: "Toy Faces by Amrit Pal",
    image: "/images/toyfaces-avatar.webp",
    cubeImage: "/images/toyfaces-cube.webp",
    contractAddress: "0x495f947276749ce646f68ac8c248420045cb7b5e", // OpenSea shared contract
    chain: "ethereum",
    // Use the correct OpenSea slug
    openSeaSlug: "toyfaces",
    // Add token ID range for the shared contract
    tokenIdRange: {
      start: "90521635264499103379939883577213779960334553668170555713715771285963183251486",
      end: "90521635264499103379939883577213779960334553668170555713715771286479654827093",
    },
    floorPrice: 0.22,
    tokenCount: 0,
    owned: false,
    addedAt: 1747612800000, // May 19, 2025
    popularity: 8,
  },

  {
    id: "mekaverse",
    name: "MekaVerse",
    image: "/images/mekaverse-avatar.webp",
    cubeImage: "/images/mekaverse-cube.webp",
    contractAddress: "0x9a534628b4062e123ce7ee2222ec20b86e16ca8f",
    chain: "ethereum",
    // Use the correct OpenSea slug
    openSeaSlug: "mekaverse",
    floorPrice: 0.028,
    tokenCount: 0,
    owned: false,
    addedAt: 1746144001000, // May 22, 2025
    popularity: 8,
  },

    // Add Lil Pudgys collection
  {
    id: "lilpudgys",
    name: "Lil Pudgys",
    image: "/images/lilpudgys-avatar.webp",
    cubeImage: "/images/lilpudgys-cube.webp",
    contractAddress: "0x524cab2ec69124574082676e6f654a18df49a048",
    chain: "ethereum",
    openSeaSlug: "lilpudgys",
    floorPrice: 1.2,
    tokenCount: 0,
    owned: false,
    addedAt: 1758297600000, // September 19, 2025, 16:00 UTC
    popularity: 5,
  },

  // Add Sam Spratt - Masks of Luci collection
  {
    id: "samspratt",
    name: "Sam Spratt — Masks of Luci",
    image: "/images/samspratt-avatar.webp",
    cubeImage: "/images/samspratt-cube.webp",
    contractAddress: "0x4440732b0d85e2a77dcb2caedfd940154241249a",
    chain: "ethereum",
    openSeaSlug: "sam-spratt-masks-of-luci",
    floorPrice: 0.95, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748034180000, // May 23, 2025
    popularity: 8,
  },

  // Add Robots collection
  {
    id: "robotos",
    name: "Robotos",
    image: "/images/robotos-avatar.webp",
    cubeImage: "/images/robotos-cube.webp",
    contractAddress: "0x099689220846644f87d1137665cded7bf3422747",
    chain: "ethereum",
    openSeaSlug: "robotos-official",
    floorPrice: 0.011, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748217600000, // May 26, 2025
    popularity: 8,
  },

  // Add Milady collection
  {
    id: "milady",
    name: "Milady Maker",
    image: "/images/milady-avatar.webp",
    cubeImage: "/images/milady-cube.webp",
    contractAddress: "0x5af0d9827e0c53e4799bb226655a1de152a425a5",
    chain: "ethereum",
    openSeaSlug: "milady",
    floorPrice: 2.5, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748217600000, // May 26, 2025
    popularity: 8,
  },

  // Add Creatures collection
  {
    id: "creatures",
    name: "Creatures",
    image: "/images/creatures-avatar.webp",
    cubeImage: "/images/creatures-cube.webp",
    contractAddress: "0xc92ceddfb8dd984a89fb494c376f9a48b999aafc",
    chain: "ethereum",
    openSeaSlug: "creatures",
    floorPrice: 0.02, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748388900000, // May 27, 2025
    popularity: 8,
  },

  // Add Rubber Duck collection
  {
    id: "rubberduck",
    name: "Rubber Duck Bath Party",
    image: "/images/rubberduck-avatar.webp",
    cubeImage: "/images/rubberduck-cube.webp",
    contractAddress: "0x7a4d1b54dd21dde804c18b7a830b5bc6e586a7f6",
    chain: "ethereum",
    openSeaSlug: "rubber-duck-bath-party",
    floorPrice: 0.02, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748388900000, // May 27, 2025
    popularity: 5,
  },

  // Add Chromie Squiggle by Snowfro collection
  {
    id: "chromie",
    name: "Chromie Squiggle by Snowfro",
    image: "/images/chromie-avatar.webp",
    cubeImage: "/images/chromie-cube.webp",
    contractAddress: "0x059edd72cd353df5106d2b9cc5ab83a52287ac3a",
    chain: "ethereum",
    openSeaSlug: "chromie-squiggle-by-snowfro",
    floorPrice: 4.00, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748548800000, // May 29, 2025
    popularity: 5,
  },

  // Add CactiCrew collection
  {
    id: "cacticrew",
    name: "CactiCrew",
    image: "/images/cacticrew-avatar.webp",
    cubeImage: "/images/cacticrew-cube.webp",
    contractAddress: "0xe0e5f8b3bfde6e883f3adc33ee9b269fae633e00",
    chain: "ethereum",
    openSeaSlug: "thecacticrew",
    floorPrice: 0.01, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748548800000, // May 29, 2025
    popularity: 6,
  },

  // Add mfers collection
  {
    id: "mfers",
    name: "mfers",
    image: "/images/mfers-avatar.webp",
    cubeImage: "/images/mfers-cube.webp",
    contractAddress: "0x79fcdef22feed20eddacbb2587640e45491b757f",
    chain: "ethereum",
    openSeaSlug: "mfers",
    floorPrice: 0.3, // Estimated floor price
    tokenCount: 0,
    owned: false,
    addedAt: 1748548800000, // May 29, 2025
    popularity: 7,
  },
  {
    id: "goblintown",
    name: "goblintown.wtf",
    image: "/images/goblintown-avatar.webp",
    cubeImage: "/images/goblintown-cube.webp",
    contractAddress: "0x8c6def540b83471664edc6d5cf75883986932674",
    chain: "ethereum",
    openSeaSlug: "goblintownwtf",
    floorPrice: 0.2,
    tokenCount: 0,
    owned: false,
    addedAt: 1749124800000, // June 05, 2025
    popularity: 6,
  },
  {
    id: "creepz",
    name: "Creepz by OVERLORD",
    image: "/images/creepz-avatar.webp",
    cubeImage: "/images/creepz-cube.webp",
    contractAddress: "0x5946aeaab44e65eb370ffaa6a7ef2218cff9b47d",
    chain: "ethereum",
    openSeaSlug: "genesis-creepz",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    addedAt: 1749124800000, // June 05, 2025
    popularity: 6,
  },
  {
    id: "boneys",
    name: "The Boneys",
    image: "/images/boneys-avatar.webp",
    cubeImage: "/images/boneys-cube.webp",
    contractAddress: "0x495f947276749ce646f68ac8c248420045cb7b5e",
    chain: "ethereum",
    openSeaSlug: "theboneys",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    addedAt: 1749124800000, // June 05, 2025
    popularity: 5,
    tokenIdRange: {
      start: "0xa057f65875263dafe39941d562b2bd6ec4c534ed000000000000000000000001",
      end:   "0xa057f65875263dafe39941d562b2bd6ec4c534edffffffffffffffffffffffff",
    },
  },
  {
    id: "nahfungible",
    name: "Nah Fungible Bones",
    image: "/images/nahfungible-avatar.webp",
    cubeImage: "/images/nahfungible-cube.webp",
    contractAddress: "0x0ee24c748445fb48028a74b0ccb6b46d7d3e3b33",
    chain: "ethereum",
    openSeaSlug: "nah-fungible-bones",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    addedAt: 1749124800000, // June 05, 2025
    popularity: 5,
  },
  {
    id: "supducks",
    name: "SupDucks",
    image: "/images/supducks-avatar.webp",
    cubeImage: "/images/supducks-cube.webp",
    contractAddress: "0x3fe1a4c1481c8351e91b64d5c398b159de07cbc5",
    chain: "ethereum",
    openSeaSlug: "supducks",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    addedAt: 1749124800000, // June 05, 2025
    popularity: 5,
  },
  {
    id: "beeple",
    name: "Beeple Everydays",
    image: "/images/beeple-avatar.webp",
    cubeImage: "/images/beeple-cube.webp",
    contractAddress: "0x6e5dc5405baefb8c0166bcc78d2692777f2cbffb",
    chain: "ethereum",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    popularity: 8,
    addedAt: 1749772800000, // June 12, 2025
  },
  {
    id: "lt3",
    name: "Less Than Three",
    image: "/images/lt3-avatar.webp",
    cubeImage: "/images/lt3-cube.webp",
    contractAddress: "0x6ff0c1e1b674138de7f5dd4131bf76665c0ff54a",
    chain: "ethereum",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1749772800000, // June 12, 2025
  },
  {
    id: "regenerates",
    name: "re:generates",
    image: "/images/regenerates-avatar.webp",
    cubeImage: "/images/regenerates-cube.webp",
    contractAddress: "0x56dfe6ae26bf3043dc8fdf33bf739b4ff4b3bc4a",
    chain: "base",
    baseContracts: ["0x56dfe6ae26bf3043dc8fdf33bf739b4ff4b3bc4a"], // Add baseContracts for Base chain support
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    popularity: 5,
    addedAt: 1749772800000, // June 12, 2025
  },
  {
    id: "thehumanoids",
    name: "The Humanoids",
    image: "/images/thehumanoids-avatar.webp",
    cubeImage: "/images/thehumanoids-cube.webp",
    contractAddress: "0x3a5051566b2241285be871f650c445a88a970edd",
    chain: "ethereum",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    popularity: 6,
    addedAt: 1749772800000, // June 12, 2025
  },
  {
    id: "murakami",
    name: "Murakami Flowers",
    image: "/images/murakami-avatar.webp",
    cubeImage: "/images/murakami-cube.webp",
    contractAddress: "0x7d8820fa92eb1584636f4f5b8515b5476b75171a",
    chain: "ethereum",
    floorPrice: 0,
    tokenCount: 0,
    owned: false,
    popularity: 7,
    addedAt: 1749772800000, // June 12, 2025
  },
  // Add Moki Genesis collection (Ronin chain)
  {
    id: "moku",
    name: "Moki Genesis",
    image: "/images/moku-avatar.jpg", // or .webp if available
    cubeImage: "/images/moku-cube.webp",
    contractAddress: "0x47b5a7c2e4f07772696bbf8c8c32fe2b9eabd550",
    chain: "ronin",
    roninContracts: ["0x47b5a7c2e4f07772696bbf8c8c32fe2b9eabd550"], // Add roninContracts for Ronin chain support
    openSeaSlug: "moki-collection",
    floorPrice: 0.02, // Can be updated later
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1750327720000, // June 19, 2025
  },
  // Add Killer GF collection
  {
    id: "killergf",
    name: "Killer GF",
    image: "/images/killergf-avatar.jpg", // or .webp if available
    cubeImage: "/images/killergf-cube.webp",
    contractAddress: "0x6be69b2a9b153737887cfcdca7781ed1511c7e36",
    chain: "ethereum",
    openSeaSlug: "killergf",
    floorPrice: 0, // Can be updated later
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1750327720000, // June 19, 2025
  },
  // Add Little Lemon Friends collection
  {
    id: "littlelemonfriends",
    name: "Little Lemon Friends",
    image: "/images/littlelemonfriends-avatar.webp",
    cubeImage: "/images/littlelemonfriends-cube.webp",
    contractAddress: "0x0b22fe0a2995c5389ac093400e52471dca8bb48a",
    chain: "ethereum",
    openSeaSlug: "little-lemon-friends",
    floorPrice: 0, // Can be updated later
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1750327720000, // June 19, 2025
  },
  // Add the#boi collection
  {
    id: "boiz",
    name: "the#boi",
    image: "/images/boiz-avatar.jpg", // or .webp, if such file exists
    cubeImage: "/images/boiz-cube.webp",
    contractAddress: "0xe03e4721c75188de6dd9ccf4fc0eb7c81399e305",
    chain: "ethereum",
    openSeaSlug: "the-boi",
    floorPrice: 0, // Can be updated later
    tokenCount: 0,
    owned: false,
    popularity: 3,
    addedAt: 1750327720000, // June 19, 2025
  },
  // Add the new collections after the existing ones
  {
    id: "thesadtimes",
    name: "The Sad Times",
    image: "/images/thesadtimes-avatar.webp",
    cubeImage: "/images/thesadtimes-cube.webp",
    contractAddress: "0xa3f7250306dbb856d8d312f93029be73343939af",
    chain: "ethereum",
    openSeaSlug: "thesadtimesbirthcertificate",
    floorPrice: 0.15,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1755619200000, // August 18, 2025, 04:00 PM
  },
  {
    id: "killabears",
    name: "KILLABEARS",
    image: "/images/killabears-avatar.webp",
    cubeImage: "/images/killabears-cube.webp",
    contractAddress: "0xc99c679c50033bbc5321eb88752e89a93e9e83c5",
    chain: "ethereum",
    openSeaSlug: "killabears",
    floorPrice: 0.12,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1755619200000, // August 18, 2025, 04:00 PM
  },
  {
    id: "basedfellas",
    name: "Based Fellas",
    image: "/images/basedfellas-avatar.webp",
    cubeImage: "/images/basedfellas-cube.webp",
    contractAddress: "0x217ec1ac929a17481446a76ff9b95b9a64f298cf",
    chain: "base",
    baseContracts: ["0x217ec1ac929a17481446a76ff9b95b9a64f298cf"], // Add baseContracts for Base chain support
    openSeaSlug: "based-fellas", // Fixed OpenSea slug
    floorPrice: 0.08,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1755619200000, // August 18, 2025, 04:00 PM
  },
  {
    id: "khuga",
    name: "Khuga",
    image: "/images/khuga-avatar.webp",
    cubeImage: "/images/khuga-cube.webp",
    contractAddress: "0xf4768ee87bd5e6a84c3bd4ae6cc9c3f4305bf376",
    chain: "ethereum",
    openSeaSlug: "khuga-by-khuga-labs",
    floorPrice: 0.25,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1755619200000, // August 18, 2025, 04:00 PM
  },
  {
    id: "starcatchers",
    name: "Starcatchers",
    image: "/images/starcatchers-avatar.webp",
    cubeImage: "/images/starcatchers-cube.webp",
    contractAddress: "0x69b9c98e8d715c25b330d0d4eb07e68cbb7f6cfc",
    chain: "ethereum",
    openSeaSlug: "starcatchersnft",
    floorPrice: 0.18,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1755619200000, // August 18, 2025, 04:00 PM
  },
  {
    id: "pillheads",
    name: "Pillheads",
    image: "/images/pillheads-avatar.webp",
    cubeImage: "/images/pillheads-cube.webp",
    contractAddress: "0x1b23d0f0f6dc3547c1b6945152acbfd6eaad85b0",
    chain: "ethereum",
    openSeaSlug: "pillheadsnft",
    floorPrice: 0.22,
    tokenCount: 0,
    owned: false,
    popularity: 4,
    addedAt: 1755619200000, // August 18, 2025, 04:00 PM
  },
  {
    id: "ens",
    name: "ENS",
    image: "/images/ens-avatar.webp",
    cubeImage: "/images/ens-cube.webp",
    contractAddress: "0x57f1887a8bf19b14fc0df6fd9b2acc9af147ea85",
    chain: "ethereum",
    openSeaSlug: "ens",
    floorPrice: 0.0,
    tokenCount: 0,
    owned: false,
    popularity: 8,
    // Use a fixed timestamp (not Date.now()) so the "New" badge expires correctly.
    // Added: 2025-09-29 (see ENS_COLLECTION_ADDITION.md)
    addedAt: 1759104000000,
  },
]

// Let's also add a safeguard to ensure unique collection IDs when updating collections

// Find the section at the end of the file where we update collection images
// Add this code after all the individual collection updates:

// Add a safeguard to ensure collection uniqueness by ID
const uniqueCollections = new Map()
COLLECTIONS.forEach((collection) => {
  // Only keep the first occurrence of each collection ID
  if (!uniqueCollections.has(collection.id)) {
    uniqueCollections.set(collection.id, collection)
  } else {
    console.warn(`Duplicate collection ID found: ${collection.id}. Keeping only the first instance.`)
  }
})

// If we found any duplicates, replace the COLLECTIONS array with the unique map values
if (uniqueCollections.size !== COLLECTIONS.length) {
  console.warn(`Fixed ${COLLECTIONS.length - uniqueCollections.size} duplicate collection entries`)
  // This line would replace COLLECTIONS, but we'll comment it out to avoid runtime errors
  // since COLLECTIONS is likely a const
  // COLLECTIONS = Array.from(uniqueCollections.values());
}

// Update the Kitaro World collection image
const kitaroCollection = COLLECTIONS.find((c) => c.id === "kitaro-world")
if (kitaroCollection) {
  kitaroCollection.cubeImage = "/images/kitaro-world-cube.webp"
}

// Update the MAYC collection image
const maycCollection = COLLECTIONS.find((c) => c.id === "mayc")
if (maycCollection) {
  maycCollection.cubeImage = "/images/mayc-cube.webp"
}

// Update the Mars Cats Voyage collection image
const marsCatsCollection = COLLECTIONS.find((c) => c.id === "marscatsvoyage")
if (marsCatsCollection) {
  marsCatsCollection.cubeImage = "/images/marscatsvoyage-cube.webp"
}

// Update the Nyan Cat collection image
const nyanCollection = COLLECTIONS.find((c) => c.id === "nyan")
if (nyanCollection) {
  nyanCollection.cubeImage = "/images/nyan-cube.webp"
}

// Update the Nakamigos collection image
const nakamigosCollection = COLLECTIONS.find((c) => c.id === "nakamigos")
if (nakamigosCollection) {
  nakamigosCollection.cubeImage = "/images/nakamigos-cube.webp"
}

// Update the Pudgy Penguins collection image
const pudgyCollection = COLLECTIONS.find((c) => c.id === "pudgy")
if (pudgyCollection) {
  pudgyCollection.cubeImage = "/images/pudgy-cube.webp"
}

// Update the Meebits collection image
const meebitsCollection = COLLECTIONS.find((c) => c.id === "meebits")
if (meebitsCollection) {
  meebitsCollection.cubeImage = "/images/meebits-cube.webp"
}

// Update the Opepen Edition collection image
const openpenCollection = COLLECTIONS.find((c) => c.id === "opepen")
if (openpenCollection) {
  openpenCollection.cubeImage = "/images/opepen-cube.webp"
}

// Update the Rare Apepes collection image
const rareApepesCollection = COLLECTIONS.find((c) => c.id === "rareapepes")
if (rareApepesCollection) {
  rareApepesCollection.cubeImage = "/images/rareapepes-cube.webp"
}

// Update the CryptoMories collection image
const cryptoMoriesCollection = COLLECTIONS.find((c) => c.id === "cryptomories")
if (cryptoMoriesCollection) {
  cryptoMoriesCollection.cubeImage = "/images/cryptomories-cube.webp"
}

// Update the Woodies collection image
const woodiesCollection = COLLECTIONS.find((c) => c.id === "woodies")
if (woodiesCollection) {
  woodiesCollection.cubeImage = "/images/woodies-cube.webp"
}

// Update the Wonderpals collection image
const wonderpalsCollection = COLLECTIONS.find((c) => c.id === "wonderpals")
if (wonderpalsCollection) {
  wonderpalsCollection.cubeImage = "/images/wonderpals-cube.webp"
}

// Update the Space Riders collection image
const spaceRidersCollection = COLLECTIONS.find((c) => c.id === "spaceriders")
if (spaceRidersCollection) {
  spaceRidersCollection.cubeImage = "/images/spaceriders-cube.webp"
}

// Update the Vinnie Hager collection image
const vinnieCollection = COLLECTIONS.find((c) => c.id === "vinnie")
if (vinnieCollection) {
  vinnieCollection.cubeImage = "/images/vinnie-cube.webp"
}

// Update the Sappy Seals collection image
const sappySealsCollection = COLLECTIONS.find((c) => c.id === "sappyseals")
if (sappySealsCollection) {
  sappySealsCollection.cubeImage = "/images/sappyseals-cube.webp"
}

// Update the Renga collection image
const rengaCollection = COLLECTIONS.find((c) => c.id === "renga")
if (rengaCollection) {
  rengaCollection.cubeImage = "/images/renga-cube.webp"
}

// Add this function at the end of the file, after all the collection updates:

// Assign addedAt dates to collections that don't have them
// This ensures all collections have a date for sorting purposes
const assignMissingDates = () => {
  // Start with a base date (January 1, 2021)
  const baseDate = new Date(2021, 0, 1).getTime()

  // Assign dates in reverse order (older collections first)
  COLLECTIONS.forEach((collection, index) => {
    if (!collection.addedAt) {
      // Add 1 day per collection to stagger the dates
      collection.addedAt = baseDate + index * 24 * 60 * 60 * 1000
    }
  })
}

// Call the function to assign dates
assignMissingDates()

// Provider for Ethereum mainnet
const provider = new ethers.JsonRpcProvider(process.env.ETHEREUM_RPC_URL || process.env.NEXT_PUBLIC_ETHEREUM_RPC_URL || "https://eth-mainnet.g.alchemy.com/v2/demo")

// Provider for Base chain
const baseProvider = new ethers.JsonRpcProvider(process.env.NEXT_PUBLIC_BASE_RPC_URL || "https://mainnet.base.org")

// API Keys
export const ALCHEMY_API_KEY = process.env.ALCHEMY_API_KEY || process.env.NEXT_PUBLIC_ALCHEMY_API_KEY
export const ALCHEMY_BASE_API_KEY = process.env.ALCHEMY_BASE_API_KEY || process.env.NEXT_PUBLIC_ALCHEMY_BASE_API_KEY
export const ETHERSCAN_API_KEY = process.env.ETHERSCAN_API_KEY || process.env.NEXT_PUBLIC_ETHERSCAN_API_KEY
export const BASESCAN_API_KEY = process.env.BASESCAN_API_KEY || process.env.NEXT_PUBLIC_BASESCAN_API_KEY
export const OPENSEA_API_KEY = process.env.OPENSEA_API_KEY || process.env.NEXT_PUBLIC_OPENSEA_API_KEY

// API Endpoints
export const ALCHEMY_BASE_URL = "https://eth-mainnet.g.alchemy.com/nft/v2/"
export const ALCHEMY_BASE_CHAIN_URL = "https://base-mainnet.g.alchemy.com/nft/v2/" // Base chain endpoint
export const ETHERSCAN_BASE_URL = "https://api.etherscan.com/api"
export const BASESCAN_BASE_URL = "https://api.basescan.org/api" // Base chain explorer API
export const OPENSEA_BASE_URL = "https://api.opensea.io/api/v1"
export const OPENSEA_V2_BASE_URL = "https://api.opensea.io/api/v2"

// Interface for Alchemy API response
export interface AlchemyNFT {
  contract: {
    address: string
  }
  id: {
    tokenId: string
  }
  title: string
  tokenUri?: {
    raw: string
    gateway: string
  }
  metadata?: {
    image?: string
    name?: string
  }
  media?: {
    raw: string
    gateway: string
  }[]
}

export interface AlchemyResponse {
  ownedNfts: AlchemyNFT[]
  totalCount: number
  blockHash: string
}

// Interface for Etherscan API response
export interface EtherscanNFTResponse {
  status: string
  message: string
  result: Array<{
    TokenAddress?: string
    TokenId?: string
    BlockNumber?: string
    TimeStamp?: string
    TokenName?: string
    TokenSymbol?: string
    contractAddress?: string
    from?: string
    to?: string
  }>
}

// Interface for Base Chain NFT
export interface BaseChainNFT {
  contractAddress: string
  tokenId: string
}

// Add interface for OpenSea API response
export interface OpenSeaAsset {
  token_id: string
  image_url?: string
  name?: string
  collection: {
    slug: string
  }
}

// ============================================================================
// DELEGATE.XYZ API INTEGRATION - NOTE: INTERFACES DEFINED ABOVE
// ============================================================================

interface OpenSeaAssetsResponse {
  assets: OpenSeaAsset[]
}

// Debug mode flag - set to false in production
const DEBUG_MODE = false

// Function to fetch NFTs from Etherscan
async function fetchEtherscanNFTs(walletAddress: string): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  const tokenData: Array<{ contractAddress: string; tokenId: string }> = []

  try {
    // Fetch ERC-721 tokens (NFTs) from Etherscan
    const erc721Url = `${ETHERSCAN_BASE_URL}?module=account&action=tokennfttx&address=${walletAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${ETHERSCAN_API_KEY}`
    const erc721Response = await fetch(erc721Url)

    if (!erc721Response.ok) {
      throw new Error(`Etherscan API error: ${erc721Response.status}`)
    }

    const erc721Data: EtherscanNFTResponse = await erc721Response.json()

    if (DEBUG_MODE) {
      console.log("Etherscan ERC-721 response status:", erc721Data.status)
      console.log("Etherscan ERC-721 result count:", erc721Data.result?.length || 0)
    }

    if (erc721Data.status === "1" && Array.isArray(erc721Data.result)) {
      // Process ERC-721 transactions to find current holdings
      // We need to track transfers in and out to determine current holdings
      const holdings = new Map<string, Set<string>>() // contractAddress -> Set of tokenIds

      erc721Data.result.forEach((tx) => {
        if (!tx) return

        const contractAddress = (
          (tx as any).contractAddress || (tx as any).TokenAddress || ""
        ).toLowerCase()
        const tokenId = (tx as any).tokenID || (tx as any).TokenId || ""

        if (!contractAddress || !tokenId) return

        // If this wallet is the recipient, add the token
        if (tx.to && tx.to.toLowerCase() === walletAddress.toLowerCase()) {
          if (!holdings.has(contractAddress)) {
            holdings.set(contractAddress, new Set())
          }
          holdings.get(contractAddress)?.add(tokenId)
        }

        // If this wallet is the sender, remove the token
        if (tx.from && tx.from.toLowerCase() === walletAddress.toLowerCase()) {
          holdings.get(contractAddress)?.delete(tokenId)
        }
      })

      // Convert the holdings map to our return format
      holdings.forEach((tokenIds, contractAddress) => {
        tokenIds.forEach((tokenId) => {
          tokenData.push({ contractAddress, tokenId })
        })
      })
    }
  } catch (error) {
    console.error("Error fetching ERC-721 tokens from Etherscan:", error)
  }

  try {
    // Fetch ERC-1155 tokens (can also be NFTs) from Etherscan
    const erc1155Url = `${ETHERSCAN_BASE_URL}?module=account&action=token1155tx&address=${walletAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${ETHERSCAN_API_KEY}`
    const erc1155Response = await fetch(erc1155Url)

    if (!erc1155Response.ok) {
      throw new Error(`Etherscan API error: ${erc1155Response.status}`)
    }

    const erc1155Data: EtherscanNFTResponse = await erc1155Response.json()

    if (DEBUG_MODE) {
      console.log("Etherscan ERC-1155 response status:", erc1155Data.status)
      console.log("Etherscan ERC-1155 result count:", erc1155Data.result?.length || 0)
    }

    if (erc1155Data.status === "1" && Array.isArray(erc1155Data.result)) {
      // Similar processing for ERC-1155 tokens
      const holdings = new Map<string, Set<string>>()

      erc1155Data.result.forEach((tx) => {
        if (!tx) return

        const contractAddress = (
          (tx as any).contractAddress || (tx as any).TokenAddress || ""
        ).toLowerCase()
        const tokenId = (tx as any).tokenID || (tx as any).TokenId || ""

        if (!contractAddress || !tokenId) return

        // If this wallet is the recipient, add the token
        if (tx.to && tx.to.toLowerCase() === walletAddress.toLowerCase()) {
          if (!holdings.has(contractAddress)) {
            holdings.set(contractAddress, new Set())
          }
          holdings.get(contractAddress)?.add(tokenId)
        }

        // If this wallet is the sender, remove the token
        if (tx.from && tx.from.toLowerCase() === walletAddress.toLowerCase()) {
          holdings.get(contractAddress)?.delete(tokenId)
        }
      })

      // Convert the holdings map to our return format
      holdings.forEach((tokenIds, contractAddress) => {
        tokenIds.forEach((tokenId) => {
          tokenData.push({ contractAddress, tokenId })
        })
      })
    }
  } catch (error) {
    console.error("Error fetching ERC-1155 tokens from Etherscan:", error)
  }

  return tokenData
}

// Updated fetchOpenSeaAssets function to use our API endpoint instead of direct OpenSea calls
async function fetchOpenSeaAssets(walletAddress: string, collectionSlug: string): Promise<OpenSeaAsset[]> {
  try {
    console.log(`Fetching ${collectionSlug} assets via our API for ${walletAddress}...`)

    // Normalize the collection slug to ensure consistent format
    const normalizedSlug = collectionSlug.toLowerCase().trim()

    // Use our API endpoint instead of direct OpenSea calls
    const response = await fetch(`/api/opensea-collection?owner=${walletAddress}&collection=${normalizedSlug}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      console.warn(`API error (${response.status}): ${response.statusText}`)
      return []
    }

    const result = await response.json()
    
    if (result.success && result.data) {
      console.log(`Found ${result.data.length} ${normalizedSlug} assets via API`)
      
      // Convert API response to OpenSeaAsset format
      return result.data.map((nft: any) => ({
        token_id: nft.tokenId || nft.identifier,
        image_url: nft.image || nft.image_url,
        name: nft.name,
        collection: {
          slug: normalizedSlug,
        },
      }))
    } else {
      console.warn(`No assets found for ${normalizedSlug} via API`)
      return []
    }
  } catch (error) {
    console.error(`Error fetching ${collectionSlug} assets via API:`, error)
    return []
  }
}



// Helper function for contract-specific approach
async function tryContractSpecificApproach(owner: string, collection: string) {
  // This would need to be implemented based on your specific needs
  // For example, you could query Alchemy for all NFTs from a specific contract
  // and then filter by token ID range

  // Special handling for Toy Faces
  if (collection.toLowerCase() === "toyfaces") {
    try {
      const toyfacesCollection = COLLECTIONS.find((c) => c.id === "toyfaces")
      if (toyfacesCollection && toyfacesCollection.tokenIdRange) {
        console.log("Trying contract-specific approach for Toy Faces...")

        // Query the OpenSea shared contract
        const url = `${ALCHEMY_BASE_URL}${ALCHEMY_API_KEY}/getNFTs/?owner=${owner}&contractAddresses[]=${toyfacesCollection.contractAddress}`
        const response = await fetch(url)

        if (response.ok) {
          const data = await response.json()

          if (data.ownedNfts && data.ownedNfts.length > 0) {
            // Filter by token ID range
            const rangeStart = BigInt(toyfacesCollection.tokenIdRange.start)
            const rangeEnd = BigInt(toyfacesCollection.tokenIdRange.end)

            const matchingAssets = data.ownedNfts
              .filter((nft: any) => {
                try {
                  const tokenIdBigInt = BigInt(nft.id.tokenId)
                  return tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd
                } catch (e) {
                  return false
                }
              })
              .map((nft: any) => ({
                token_id: nft.id.tokenId,
                image_url: nft.metadata?.image || "",
                name: nft.title || `#${nft.id.tokenId}`,
                collection: {
                  slug: "toyfaces",
                },
              }))

            if (matchingAssets.length > 0) {
              console.log(`Found ${matchingAssets.length} Toy Faces assets via contract filtering`)
              return { success: true, assets: matchingAssets }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error in Toy Faces contract-specific approach:", error)
    }
  }

  // For now, return a failure for other collections
  return { success: false, error: "Contract-specific approach not implemented" }
}

export { fetchOpenSeaAssets }

// Update the fetchWalletCollections function to check both Ethereum and Base chains
// Update the fetchWalletCollections function to include timeout
// Find the fetchWalletCollections function and update it:

/**
 * Unified function to get user's NFT collections
 * This is the single source of truth for collection data used by both frontend and API
 *
 * @param walletAddress - The wallet address to check for NFT ownership
 * @param onProgress - Optional progress callback
 * @returns Promise<NFTCollection[]> - Array of owned collections with metadata
 */
export async function getUserCollections(
  walletAddress: string,
  onProgress?: (progress: { current: number; total: number; collection: string }) => void
): Promise<NFTCollection[]> {
  // Use the optimized function with delegated wallets support
  return await checkCollectionsWithDelegatedWallets(walletAddress, onProgress)
}

/**
 * Check data consistency between frontend and API for a wallet
 * This helps identify discrepancies between what users see and what APIs return
 *
 * @param walletAddress - The wallet address to check
 * @param tokenId - Optional token ID to check specific totem data
 * @returns Promise with consistency check results
 */
export async function checkDataConsistency(
  walletAddress: string,
  tokenId?: number
): Promise<{
  isConsistent: boolean
  frontendCollections: NFTCollection[]
  apiCollections: NFTCollection[]
  kvData?: any
  issues: string[]
}> {
  const issues: string[] = []

  try {
    // Get collections from the same source as frontend
    const frontendCollections = await getUserCollections(walletAddress)
    const ownedCollections = frontendCollections.filter(c => c.owned)

    // Get collections from API (simulating what API would return)
    const apiCollections = await getUserCollections(walletAddress)

    // Check if collections match
    const frontendIds = ownedCollections.map(c => c.id).sort()
    const apiIds = apiCollections.filter(c => c.owned).map(c => c.id).sort()

    if (JSON.stringify(frontendIds) !== JSON.stringify(apiIds)) {
      issues.push(`Collection mismatch: Frontend shows [${frontendIds.join(', ')}] but API returns [${apiIds.join(', ')}]`)
    }

    // If tokenId provided, check KV data consistency
    let kvData: any = undefined
    if (tokenId) {
      try {
        const { kv } = await import('@vercel/kv')
        const { namespaceKey } = await import('./kv-namespace')

        // Check token mapping
        const tokenMapping = await kv.get(namespaceKey('totem:token:' + tokenId))
        if (tokenMapping && tokenMapping !== walletAddress.toLowerCase()) {
          issues.push(`Token mapping mismatch: KV shows token ${tokenId} belongs to ${tokenMapping}, but checking wallet ${walletAddress}`)
        }

        // Check totem data
        kvData = await kv.get(namespaceKey('totem:' + walletAddress.toLowerCase()))
        if (kvData) {
          const kvCollectionIds = Array.isArray(kvData.collections)
            ? kvData.collections.map((c: any) => typeof c === 'string' ? c : c.id)
            : []

          if (JSON.stringify(kvCollectionIds.sort()) !== JSON.stringify(frontendIds.sort())) {
            issues.push(`KV data mismatch: KV shows collections [${kvCollectionIds.join(', ')}] but frontend shows [${frontendIds.join(', ')}]`)
          }
        } else {
          issues.push('No KV data found for this wallet')
        }
      } catch (error) {
        issues.push(`Error checking KV data: ${error}`)
      }
    }

    return {
      isConsistent: issues.length === 0,
      frontendCollections: ownedCollections,
      apiCollections: apiCollections.filter(c => c.owned),
      kvData,
      issues
    }

  } catch (error) {
    issues.push(`Error during consistency check: ${error}`)
    return {
      isConsistent: false,
      frontendCollections: [],
      apiCollections: [],
      issues
    }
  }
}

/**
 * Auto-sync data for a wallet if inconsistencies are detected
 * This ensures that KV data matches the current collection state
 *
 * @param walletAddress - The wallet address to sync
 * @param tokenId - The token ID to sync
 * @returns Promise with sync results
 */
export async function autoSyncWalletData(
  walletAddress: string,
  tokenId: number
): Promise<{
  synced: boolean
  oldCollections: string[]
  newCollections: string[]
  error?: string
}> {
  try {
    console.log(`[AUTO-SYNC] Starting sync for wallet ${walletAddress}, token ${tokenId}`)

    // Get current collections from the unified source
    const collections = await getUserCollections(walletAddress)
    const ownedCollections = collections.filter(c => c.owned)
    const collectionIds = ownedCollections.map(c => c.id)

    if (collectionIds.length === 0) {
      return {
        synced: false,
        oldCollections: [],
        newCollections: [],
        error: 'No collections found for this wallet'
      }
    }

    // Check current KV data
    const { kv } = await import('@vercel/kv')
    const { namespaceKey } = await import('./kv-namespace')

    const totemData = await kv.get(namespaceKey('totem:' + walletAddress.toLowerCase())) as any
    const oldCollections = totemData && Array.isArray(totemData.collections)
      ? totemData.collections.map((c: any) => typeof c === 'string' ? c : c.id)
      : []

    // Check if data needs updating
    if (JSON.stringify(oldCollections.sort()) === JSON.stringify(collectionIds.sort())) {
      console.log(`[AUTO-SYNC] Data already up to date for wallet ${walletAddress}`)
      return {
        synced: false,
        oldCollections,
        newCollections: collectionIds,
        error: 'Data already up to date'
      }
    }

    // IMPORTANT: When syncing, write snapshots (CollectionSnapshot[]) so seasonal cubeImage is preserved.
    const { getCubeImageForSnapshot } = await import('./seasonal-images')
    const { getDisplayNameFromSlug } = await import('./opensea-utils')
    const snapshots = collectionIds.map((collectionId: string) => {
      // Prefer display name mapping, then COLLECTIONS name, then id
      const mapped = getDisplayNameFromSlug(collectionId)
      const col = COLLECTIONS.find(c => c.id === collectionId)
      const name = mapped !== collectionId ? mapped : (col?.name || collectionId)
      return {
        id: collectionId,
        name,
        cubeImage: getCubeImageForSnapshot(collectionId),
      }
    })

    // Update KV data
    const now = new Date().toISOString()
    const currentVersion = (totemData?.version || 1)
    const nextVersion = currentVersion + 1

    const updatedData = {
      ...(totemData || {}),
      walletAddress: walletAddress.toLowerCase(),
      tokenId,
      collections: snapshots,
      blockCount: snapshots.length,
      lastUpdated: now,
      version: nextVersion,
      versions: [
        ...(totemData?.versions || []),
        {
          v: nextVersion,
          collections: snapshots,
          blockCount: snapshots.length,
          createdAt: now
        }
      ]
    }

    await kv.set(namespaceKey('totem:' + walletAddress.toLowerCase()), updatedData)
    await kv.set(namespaceKey('totem:token:' + tokenId), walletAddress.toLowerCase())

    console.log(`[AUTO-SYNC] Successfully synced wallet ${walletAddress}: ${oldCollections.join(', ')} → ${collectionIds.join(', ')}`)

    return {
      synced: true,
      oldCollections,
      newCollections: collectionIds
    }

  } catch (error) {
    console.error(`[AUTO-SYNC] Error syncing wallet ${walletAddress}:`, error)
    return {
      synced: false,
      oldCollections: [],
      newCollections: [],
      error: `Sync failed: ${error}`
    }
  }
}

/**
 * Fetches all NFT collections owned by a wallet address (legacy function)
 *
 * @param walletAddress - The wallet address to check for NFT ownership
 * @returns Promise<NFTCollection[]> - Array of owned collections with metadata
 *
 * Note: This is a legacy function. For better performance, use checkCollectionsInParallelWithProgress
 * from optimized-api.ts which performs parallel API calls across multiple blockchains.
 *
 * @deprecated Use getUserCollections instead for consistency
 */
export async function fetchWalletCollections(walletAddress: string): Promise<NFTCollection[]> {
  try {
    // Validate the wallet address
    if (!ethers.isAddress(walletAddress)) {
      throw new Error("Invalid Ethereum address")
    }

    // For demo addresses, use simulated data
    if (walletAddress.startsWith("0x123456789")) {
      console.warn("Simulated collections have been disabled for security reasons")
      return []
    }

    // Create a map to count NFTs by contract address
    const contractCounts: Record<string, number> = {}
    // Create a set to track all contract addresses found (for debugging)
    const allFoundContracts = new Set<string>()
    // Create a map to track token IDs for each contract to avoid duplicates
    const tokenIdsByContract: Record<string, Set<string>> = {}
    // Track which chain each contract belongs to
    const contractChains: Record<string, "ethereum" | "base" | "ronin"> = {}

    // Fetch NFTs from Ethereum chain with timeout
    try {
      const alchemyDataPromise = fetchAlchemyNFTs(walletAddress)
      // Set a 30-second timeout for Alchemy API
      const alchemyData = await withTimeout(alchemyDataPromise, 30000)

      // Count NFTs by contract address from Alchemy
      if (alchemyData && alchemyData.ownedNfts) {
        alchemyData.ownedNfts.forEach((nft) => {
          if (!nft || !nft.contract || !nft.contract.address || !nft.id || !nft.id.tokenId) {
            return // Skip invalid NFT data
          }

          const contractAddress = nft.contract.address.toLowerCase()
          const tokenId = nft.id.tokenId

          // Initialize token ID set if it doesn't exist
          if (!tokenIdsByContract[contractAddress]) {
            tokenIdsByContract[contractAddress] = new Set()
          }

          // Only count this token if we haven't seen it before
          if (!tokenIdsByContract[contractAddress].has(tokenId)) {
            tokenIdsByContract[contractAddress].add(tokenId)
            contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
            allFoundContracts.add(contractAddress)
            contractChains[contractAddress] = "ethereum"
          }
        })
      } else {
        console.warn("Alchemy returned invalid data structure:", alchemyData)
      }
    } catch (error) {
      console.error("Error fetching NFTs from Alchemy:", error)
      // Continue with other data sources
    }

    // Supplement with Etherscan data with timeout
    try {
      const etherscanDataPromise = fetchEtherscanNFTs(walletAddress)
      // Set a 20-second timeout for Etherscan API
      const etherscanData = await withTimeout(etherscanDataPromise, 20000)

      if (etherscanData && etherscanData.length > 0) {
        // Process Etherscan data
        for (const item of etherscanData) {
          if (!item.contractAddress || !item.tokenId) continue

          const contractAddress = item.contractAddress.toLowerCase()
          const tokenId = item.tokenId

          // Initialize token ID set if it doesn't exist
          if (!tokenIdsByContract[contractAddress]) {
            tokenIdsByContract[contractAddress] = new Set()
          }

          // Only count this token if we haven't seen it before
          if (!tokenIdsByContract[contractAddress].has(tokenId)) {
            tokenIdsByContract[contractAddress].add(tokenId)
            contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
            allFoundContracts.add(contractAddress)
            contractChains[contractAddress] = "ethereum"
          }
        }

        if (DEBUG_MODE) {
          console.log("Found NFTs from Etherscan:", Object.keys(tokenIdsByContract))
          console.log("Updated token counts after Etherscan:", contractCounts)
        }
      } else {
        console.log("No NFTs found from Etherscan")
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("timed out")) {
        console.error("Etherscan API request timed out:", error)
      } else {
        console.error("Error fetching NFTs from Etherscan:", error)
      }
      // Continue with whatever data we have from Alchemy
    }

    // In the fetchWalletCollections function, improve the Base chain NFT detection

    // Find the section where Base chain NFTs are checked and update it to:

    // Fetch NFTs from Base chain with improved error handling
    try {
      console.log("Checking for Base chain NFTs...")
      const baseData = await fetchBaseChainNFTsUtil(walletAddress)

      if (baseData && baseData.length > 0) {
        console.log(`Found ${baseData.length} NFTs on Base chain`)

        // Process Base chain data
        for (const item of baseData) {
          if (!item.contractAddress || !item.tokenId) continue

          const contractAddress = item.contractAddress.toLowerCase()
          const tokenId = item.tokenId

          console.log(`Found Base NFT: ${contractAddress} - Token ID: ${tokenId}`)

          // Initialize token ID set if it doesn't exist
          if (!tokenIdsByContract[contractAddress]) {
            tokenIdsByContract[contractAddress] = new Set()
          }

          // Only count this token if we haven't seen it before
          if (!tokenIdsByContract[contractAddress].has(tokenId)) {
            tokenIdsByContract[contractAddress].add(tokenId)
            contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
            allFoundContracts.add(contractAddress)
            contractChains[contractAddress] = "base"
          }
        }

        if (DEBUG_MODE) {
          console.log(
            "Found NFTs from Base chain:",
            baseData.map((item) => item.contractAddress),
          )
          console.log("Updated token counts after Base chain:", contractCounts)
        }
      } else {
        console.log("No NFTs found from Base chain")
      }
    } catch (error) {
      console.error("Error fetching NFTs from Base chain:", error)
      // Continue with whatever data we have from Ethereum
    }

    // Fetch NFTs from Ronin chain
    try {
      // Check if any collections have roninContracts defined
      const hasRoninCollections = COLLECTIONS.some((c) => c.chain === "ronin")

      // Only fetch Ronin chain NFTs if we have collections with roninContracts
      if (hasRoninCollections) {
        const roninData = await fetchRoninChainNFTs(walletAddress)

        if (roninData && roninData.length > 0) {
          // Process Ronin chain data
          for (const item of roninData) {
            if (!item.contractAddress || !item.tokenId) continue

            const contractAddress = item.contractAddress.toLowerCase()
            const tokenId = item.tokenId

            // Initialize token ID set if it doesn't exist
            if (!tokenIdsByContract[contractAddress]) {
              tokenIdsByContract[contractAddress] = new Set()
            }

            // Only count this token if we haven't seen it before
            if (!tokenIdsByContract[contractAddress].has(tokenId)) {
              tokenIdsByContract[contractAddress].add(tokenId)
              contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
              allFoundContracts.add(contractAddress)
              contractChains[contractAddress] = "ronin"
            }
          }

          if (DEBUG_MODE) {
            console.log(
              "Found NFTs from Ronin chain:",
              roninData.map((item) => item.contractAddress),
            )
            console.log("Updated token counts after Ronin chain:", contractCounts)
          }
        } else {
          console.log("No NFTs found from Ronin chain")
        }
      }
    } catch (error) {
      console.error("Error fetching NFTs from Ronin chain:", error)
      // Continue with whatever data we have from Ethereum and Base
    }

    // DIRECT OpenSea API integration for collections with openSeaSlug
    // This is especially important for shared contracts like Transmissions909
    try {
      // Get collections with openSeaSlug
      const openSeaCollections = COLLECTIONS.filter((c) => c.openSeaSlug)

      for (const collection of openSeaCollections) {
        if (!collection.openSeaSlug) continue

        console.log(`Directly checking OpenSea for ${collection.name} (${collection.openSeaSlug})...`)

        // Fetch assets directly from OpenSea API
        const assets = await fetchOpenSeaAssets(walletAddress, collection.openSeaSlug)

        if (assets.length > 0) {
          console.log(`Found ${assets.length} ${collection.name} assets from OpenSea API`)

          // Get the contract address
          const contractAddress = collection.contractAddress.toLowerCase()

          // Initialize token ID set if it doesn't exist
          if (!tokenIdsByContract[contractAddress]) {
            tokenIdsByContract[contractAddress] = new Set()
          }

          // Add each token to our tracking
          assets.forEach((asset) => {
            const tokenId = asset.token_id

            if (!tokenIdsByContract[contractAddress].has(tokenId)) {
              tokenIdsByContract[contractAddress].add(tokenId)
              contractCounts[contractAddress] = (contractCounts[contractAddress] || 0) + 1
              allFoundContracts.add(contractAddress)
              contractChains[contractAddress] = "ethereum"

              console.log(`Added ${collection.name} token ${tokenId} from OpenSea API`)
            }
          })
        } else {
          console.log(`No ${collection.name} assets found from OpenSea API`)
        }
      }
    } catch (error) {
      console.error("Error in direct OpenSea API integration:", error)
    }

    // If we couldn't get any data from any API, fall back to simulated data
    if (Object.keys(contractCounts).length === 0) {
      console.warn("No NFT data found from APIs")

      // If we couldn't find any collections, check if this is a known issue with the API
      // and provide a fallback for testing
      const ownedCollections: NFTCollection[] = []
      if (ownedCollections.length === 0 && process.env.NODE_ENV === "production") {
        console.warn("No collections found for wallet. Checking if this is a known wallet with collections...")

        // Check if this wallet is known to have collections but our API calls failed
        const knownWallets = [
          "0x8fa217251049c207Eee65f0a2eAa2D5C4BF4e5D0", // Example wallet known to have Azuki
          "0xd387a6e4e84a6c86bd90c158c6028a58cc8ac459", // Example wallet known to have BAYC
          // Add more known wallets if needed
        ]

        if (knownWallets.some((w) => w.toLowerCase() === walletAddress.toLowerCase())) {
          console.log("This is a known wallet. Attempting direct collection checks...")

          // Try direct contract checks for popular collections
          for (const collection of COLLECTIONS.filter((c) => (c.popularity ?? 0) > 8)) {
            try {
              const isOwned = await manuallyCheckCollection(walletAddress, collection.id)
              if (isOwned) {
                console.log(`Direct check confirmed ownership of ${collection.name}`)
                collection.owned = true
                collection.tokenCount = 1
                ownedCollections.push(collection)
              }
            } catch (err) {
              console.error(`Error in direct check for ${collection.id}:`, err)
            }
          }
        }
      }
      return []
    }

    // Process the NFTs and match them against our known collections
    const collectionsWithOwnership = COLLECTIONS.map((collection) => ({ ...collection }))

    // Match contract addresses to our collections
    collectionsWithOwnership.forEach((collection) => {
      const contractAddress = collection.contractAddress.toLowerCase()
      const relatedAddresses = (collection.relatedContracts || []).map((addr) => addr.toLowerCase())

      // Skip Base chain collections until we're ready to support them
      // But allow specific collections with baseContracts to use Base chain
      if (collection.chain === "base" && !collection.baseContracts) {
        collection.owned = false
        collection.tokenCount = 0
        return
      }

      // For shared contracts (like OpenSea's), we need to check token IDs
      if (collection.tokenIdRange) {
        // Check if any tokens from this contract are within the specified range
        const tokensFromContract = tokenIdsByContract[contractAddress] || new Set()
        let matchingTokenCount = 0

        // Convert range boundaries to BigInt for proper comparison of large token IDs
        const rangeStart = BigInt(collection.tokenIdRange.start)
        const rangeEnd = BigInt(collection.tokenIdRange.end)

        if (DEBUG_MODE) {
          console.log(`Checking ${collection.name} token range:`, {
            start: collection.tokenIdRange.start,
            end: collection.tokenIdRange.end,
            tokensCount: tokensFromContract.size,
          })

          // Log all token IDs for debugging
          if (tokensFromContract.size > 0) {
            console.log(`Token IDs for ${collection.contractAddress}:`, Array.from(tokensFromContract))
          }
        }

        // Check each token ID to see if it's in the range for this collection
        tokensFromContract.forEach((tokenId) => {
          try {
            const tokenIdBigInt = BigInt(tokenId)
            if (tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd) {
              matchingTokenCount++
              if (DEBUG_MODE) {
                console.log(`Found matching token for ${collection.name}:`, tokenId)
              }
            }
          } catch (e) {
            // Skip if token ID can't be converted to BigInt
            console.error(`Error comparing token ID ${tokenId}:`, e)
          }
        })

        if (matchingTokenCount > 0) {
          collection.owned = true
          collection.tokenCount = matchingTokenCount
          if (DEBUG_MODE) {
            console.log(`${collection.name} is owned with ${matchingTokenCount} tokens`)
          }
        }

        // Also check related contracts the same way
        relatedAddresses.forEach((relatedAddr) => {
          const tokensFromRelated = tokenIdsByContract[relatedAddr] || new Set()
          let relatedMatchingCount = 0

          tokensFromRelated.forEach((tokenId) => {
            try {
              const tokenIdBigInt = BigInt(tokenId)
              if (tokenIdBigInt >= rangeStart && tokenIdBigInt <= rangeEnd) {
                relatedMatchingCount++
                if (DEBUG_MODE) {
                  console.log(`Found matching token for ${collection.name} in related contract:`, tokenId)
                }
              }
            } catch (e) {
              console.error(`Error comparing token ID ${tokenId} for related contract:`, e)
            }
          })

          if (relatedMatchingCount > 0) {
            collection.owned = true
            collection.tokenCount = (collection.tokenCount || 0) + relatedMatchingCount
            if (DEBUG_MODE) {
              console.log(
                `${collection.name} is owned with ${relatedMatchingCount} additional tokens from related contract`,
              )
            }
          }
        })
      } else {
        // Standard contract check (non-shared contracts)
        let totalTokenCount = 0
        let foundInAnyContract = false
        
        // Check main contract address
        if (contractCounts[contractAddress]) {
          totalTokenCount += contractCounts[contractAddress]
          foundInAnyContract = true
          console.log(`[COLLECTION-MATCH] ${collection.name}: found ${contractCounts[contractAddress]} tokens in main contract ${contractAddress}`)
        }

        // Check related contract addresses
        relatedAddresses.forEach((relatedAddr) => {
          if (contractCounts[relatedAddr]) {
            totalTokenCount += contractCounts[relatedAddr]
            foundInAnyContract = true
            console.log(`[COLLECTION-MATCH] ${collection.name}: found ${contractCounts[relatedAddr]} tokens in related contract ${relatedAddr}`)
          }
        })

        // Check Base chain contract addresses
        const baseAddresses = (collection.baseContracts || []).map((addr) => addr.toLowerCase())
        baseAddresses.forEach((baseAddr) => {
          if (contractCounts[baseAddr]) {
            totalTokenCount += contractCounts[baseAddr]
            foundInAnyContract = true
            console.log(`[COLLECTION-MATCH] ${collection.name}: found ${contractCounts[baseAddr]} tokens in Base contract ${baseAddr}`)
          }
        })
        
        if (foundInAnyContract) {
          collection.owned = true
          collection.tokenCount = totalTokenCount
          console.log(`[COLLECTION-MATCH] ${collection.name}: TOTAL ${totalTokenCount} tokens owned`)
        }
      }
    })

    // Special handling for Transmissions909 - force check with OpenSea API
    const transmissions909Collection = collectionsWithOwnership.find((c) => c.id === "transmissions909")
    if (transmissions909Collection) {
      try {
        console.log("Special handling for Transmissions909...")
        const assets = await fetchOpenSeaAssets(walletAddress, "transmissions909")

        if (assets.length > 0) {
          console.log(`Found ${assets.length} Transmissions909 assets from OpenSea API`)
          transmissions909Collection.owned = true
          transmissions909Collection.tokenCount = assets.length
        }
      } catch (error) {
        console.error("Error in special Transmissions909 handling:", error)
      }
    }

    // Find the section where special handling for Transmissions909 is implemented
    // Add similar handling for Toy Faces after that section:

    // Special handling for Toy Faces - force check with OpenSea API
    const toyfacesCollection = collectionsWithOwnership.find((c) => c.id === "toyfaces")
    if (toyfacesCollection) {
      try {
        console.log("Special handling for Toy Faces...")
        const assets = await fetchOpenSeaAssets(walletAddress, "toyfaces")

        if (assets.length > 0) {
          console.log(`Found ${assets.length} Toy Faces assets from OpenSea API`)
          toyfacesCollection.owned = true
          toyfacesCollection.tokenCount = assets.length
        }
      } catch (error) {
        console.error("Error in special Toy Faces handling:", error)
      }
    }

    // Special handling for Based Minis - direct contract check
    const basedMinisCollection = collectionsWithOwnership.find((c) => c.id === "based-minis")
    if (basedMinisCollection) {
      try {
        console.log("Special handling for Based Minis...")

        // Create a minimal ERC721 interface to check balance
        const erc721Interface = new ethers.Interface(["function balanceOf(address owner) view returns (uint256)"])

        const contractAddress = "0xd683f8f08aca669bf9207d72dceb6bac063c34a2" // Based Minis contract
        const contract = new ethers.Contract(contractAddress, erc721Interface, baseProvider)

        // Check if wallet owns any tokens from this collection
        let balanceNumber = 0
        try {
          // Add null check for contract and provider
          if (!contract || !contract.balanceOf) {
            console.warn(`Contract not properly initialized for Based Minis`)
            return []
          }
          const balance = await contract.balanceOf(walletAddress)
          balanceNumber = Number(balance)
        } catch (err) {
          console.warn(`Error calling balanceOf on Based Minis contract (${contractAddress}):`, err)
        }
        if (balanceNumber > 0) {
          console.log(`Found ${balanceNumber} Based Minis tokens via direct contract check`)
          basedMinisCollection.owned = true
          basedMinisCollection.tokenCount = balanceNumber
        }
      } catch (error) {
        console.error("Error in special Based Minis handling:", error)
      }
    }

    // Special handling for re:generates (Base chain) - direct contract check
    const regeneratesCollection = collectionsWithOwnership.find((c) => c.id === "regenerates")
    if (regeneratesCollection) {
      try {
        console.log("Special handling for re:generates (Base chain)...")
        const erc721Interface = new ethers.Interface(["function balanceOf(address owner) view returns (uint256)"])
        const contractAddress = "0x56dfe6ae26bf3043dc8fdf33bf739b4ff4b3bc4a" // re:generates contract
        const contract = new ethers.Contract(contractAddress, erc721Interface, baseProvider)
        let balanceNumber = 0
        try {
          // Add null check for contract and provider
          if (!contract || !contract.balanceOf) {
            console.warn(`Contract not properly initialized for re:generates`)
            return []
          }
          const balance = await contract.balanceOf(walletAddress)
          balanceNumber = Number(balance)
        } catch (err) {
          console.warn(`Error calling balanceOf on re:generates contract (${contractAddress}):`, err)
        }
        if (balanceNumber > 0) {
          console.log(`Found ${balanceNumber} re:generates tokens via direct contract check`)
          regeneratesCollection.owned = true
          regeneratesCollection.tokenCount = balanceNumber
        }
      } catch (error) {
        console.error("Error in special re:generates handling:", error)
      }
    }

    // NOTE: Removed special Grifters handler - now handled by unified collection matching with proper relatedContracts support

    // Filter to only return owned collections (will be refreshed after special handlers)
    let ownedCollections = collectionsWithOwnership.filter((collection) => collection.owned && !collection.hidden)

    // If we couldn't find any collections, check if this is a known issue with the API
    // and provide a fallback for testing
    if (ownedCollections.length === 0 && process.env.NODE_ENV === "production") {
      console.warn("No collections found for wallet. Checking if this is a known wallet with collections...")

      // Check if this wallet is known to have collections but our API calls failed
      const knownWallets = [
        "0x8fa217251049c207Eee65f0a2eAa2D5C4BF4e5D0", // Example wallet known to have Azuki
        "0xd387a6e4e84a6c86bd90c158c6028a58cc8ac459", // Example wallet known to have BAYC
        // Add more known wallets if needed
      ]

      if (knownWallets.some((w) => w.toLowerCase() === walletAddress.toLowerCase())) {
        console.log("This is a known wallet. Attempting direct collection checks...")

        // Try direct contract checks for popular collections
        for (const collection of COLLECTIONS.filter((c) => (c.popularity ?? 0) > 8)) {
          try {
            const isOwned = await manuallyCheckCollection(walletAddress, collection.id)
            if (isOwned) {
              console.log(`Direct check confirmed ownership of ${collection.name}`)
              collection.owned = true
              collection.tokenCount = 1
              ownedCollections.push(collection)
            }
          } catch (err) {
            console.error(`Error in direct check for ${collection.id}:`, err)
          }
        }
      }
    }

    // If no collections match, log a message with all found contracts for debugging
    if (ownedCollections.length === 0 && Object.keys(contractCounts).length > 0) {
      console.log(
        `Wallet has NFTs from ${Object.keys(contractCounts).length} contracts, but none match our known collections`,
      )

      if (DEBUG_MODE) {
        console.log("All found contract addresses:", Array.from(allFoundContracts))
        console.log(
          "Our tracked contract addresses:",
          COLLECTIONS.map((c) => c.contractAddress.toLowerCase()).concat(
            COLLECTIONS.flatMap((c) => (c.relatedContracts || []).map((addr) => addr.toLowerCase())),
          ),
        )
      }
    }

    // Grifters: treat as regular ERC-721 (like other collections), no separate logic here.

    // Refresh owned collections after special handlers to capture updates
    ownedCollections = collectionsWithOwnership.filter((collection) => collection.owned && !collection.hidden)

    // Note: Removed FINAL VALIDATION for suspicious collections (was used for Grifters phantom detection)
    console.log(`Found ${ownedCollections.length} owned collections: ${ownedCollections.map(c => c.id).join(', ')}`)
    
    return ownedCollections
  } catch (error) {
    console.error("Error fetching NFT collections:", error)
    throw error
  }
}

// Function to fetch NFTs from Alchemy
async function fetchAlchemyNFTs(walletAddress: string): Promise<AlchemyResponse> {
  try {
    // Try to use configured API key first
    let apiKey = ALCHEMY_API_KEY

    // Fallback to demo key if not configured (for development/testing)
    if (!apiKey || apiKey === "demo") {
      console.log("🔄 [FALLBACK] Using Alchemy demo API key")
      apiKey = "demo"
    }

    // First, try to get all NFTs in one request
    const url = `${ALCHEMY_BASE_URL}${apiKey}/getNFTs/?owner=${walletAddress}&pageSize=100`
    const response = await fetch(url)

    if (!response.ok) {
      console.warn(`⚠️ [FALLBACK] Alchemy API error with key: ${response.status}, trying demo key`)
      // Try with demo key as fallback
      const demoUrl = `${ALCHEMY_BASE_URL}demo/getNFTs/?owner=${walletAddress}&pageSize=100`
      const demoResponse = await fetch(demoUrl)

      if (!demoResponse.ok) {
        throw new Error(`Alchemy API error: ${demoResponse.status}`)
      }

      const data = await demoResponse.json()
      console.log(`✅ [FALLBACK] Alchemy demo API found ${data.totalCount} NFTs`)
      return data
    }

  const data = await response.json()

  if (DEBUG_MODE) {
    console.log(`Alchemy found ${data.totalCount} NFTs for wallet ${walletAddress}`)
  }

  // If there are more than 100 NFTs, we need to paginate
  if (data.totalCount > 100) {
    let allNfts = [...data.ownedNfts]
    let pageKey = data.pageKey
      let retryCount = 0
      const maxRetries = 3

      // Continue fetching pages until we have all NFTs or hit max retries
      while (pageKey && retryCount < maxRetries) {
        try {
      const pageUrl = `${ALCHEMY_BASE_URL}${ALCHEMY_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=100&pageKey=${pageKey}`
      const pageResponse = await fetch(pageUrl)

      if (!pageResponse.ok) {
            console.warn(`Error fetching page with pageKey ${pageKey}: ${pageResponse.status}`)
        break
      }

      const pageData = await pageResponse.json()
      allNfts = [...allNfts, ...pageData.ownedNfts]
      pageKey = pageData.pageKey // Will be undefined when we reach the last page

      if (DEBUG_MODE) {
        console.log(`Fetched additional page with ${pageData.ownedNfts.length} NFTs`)
          }
          
          // Reset retry count on successful fetch
          retryCount = 0
        } catch (error) {
          console.warn(`Error fetching page with pageKey ${pageKey}:`, error)
          retryCount++
          
          if (retryCount >= maxRetries) {
            console.warn(`Max retries (${maxRetries}) reached for pagination. Returning partial results.`)
            break
          }
          
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount))
      }
    }

    // Return the combined data
    return {
      ownedNfts: allNfts,
      totalCount: allNfts.length,
      blockHash: data.blockHash,
    }
  }

    console.log(`✅ [FALLBACK] Alchemy API found ${data.totalCount} NFTs`)
    return data
  } catch (error) {
    console.error("❌ [FALLBACK] Error in fetchAlchemyNFTs:", error)
    // Return empty response instead of throwing
    return {
      ownedNfts: [],
      totalCount: 0,
      blockHash: "",
    }
  }
}

// Function to fetch NFTs from Base Chain
async function fetchBaseChainNFTs(walletAddress: string): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  const tokenData: Array<{ contractAddress: string; tokenId: string }> = []

  try {
    // First try using Alchemy's Base chain endpoint if available
    let baseApiKey = ALCHEMY_BASE_API_KEY

    // Fallback to demo key if not configured
    if (!baseApiKey || baseApiKey === "demo") {
      console.log("🔄 [FALLBACK] Using Base demo API key")
      baseApiKey = "demo"
    }

    try {
      const url = `${ALCHEMY_BASE_CHAIN_URL}${baseApiKey}/getNFTs/?owner=${walletAddress}&pageSize=100`
      const response = await fetch(url)

      if (response.ok) {
        const data = await response.json()

        if (DEBUG_MODE) {
          console.log(`Alchemy Base chain found ${data.totalCount} NFTs for wallet ${walletAddress}`)
        }

        // Process NFTs from Alchemy Base chain
        data.ownedNfts.forEach((nft: AlchemyNFT) => {
          tokenData.push({
            contractAddress: nft.contract.address.toLowerCase(),
            tokenId: nft.id.tokenId,
          })
        })

        // Handle pagination if needed
        if (data.totalCount > 100 && data.pageKey) {
          let pageKey = data.pageKey

          while (pageKey) {
            const pageUrl = `${ALCHEMY_BASE_CHAIN_URL}${ALCHEMY_BASE_API_KEY}/getNFTs/?owner=${walletAddress}&pageSize=100&pageKey=${pageKey}`
            const pageResponse = await fetch(pageUrl)

            if (!pageResponse.ok) break

            const pageData = await pageResponse.json()
            pageData.ownedNfts.forEach((nft: AlchemyNFT) => {
              tokenData.push({
                contractAddress: nft.contract.address.toLowerCase(),
                tokenId: nft.id.tokenId,
              })
            })

            pageKey = pageData.pageKey
          }
        }
      }
    } catch (error) {
      console.error("Error fetching NFTs from Alchemy Base chain:", error)
    }

    // If we couldn't get data from Alchemy, try using the Base provider directly
    if (tokenData.length === 0) {
      try {
        // This is a simplified approach - in a real app, you'd need to implement
        // contract scanning or use a more comprehensive API for Base chain

        // For known Base collections, check if the wallet owns any tokens
        const baseCollections = COLLECTIONS.filter((c) => c.chain === "base")

        for (const collection of baseCollections) {
          try {
            // Create a minimal ERC721 interface to check balance
            const erc721Interface = new ethers.Interface([
              "function balanceOf(address owner) view returns (uint256)",
              "function tokenOfOwnerByIndex(address owner, uint256 index) view returns (uint256)",
            ])

            const contract = new ethers.Contract(collection.contractAddress, erc721Interface, baseProvider)

            // Check if the wallet owns any tokens from this collection
            // Add null check for contract and provider
            if (!contract || !contract.balanceOf) {
              console.warn(`Contract not properly initialized for ${collection.name}`)
              return []
            }
            const balance = await contract.balanceOf(walletAddress)

            if (balance > 0) {
              // For each token, add it to our results
              for (let i = 0; i < Math.min(balance, 10); i++) {
                // Limit to 10 tokens per collection
                try {
                  const tokenId = await contract.tokenOfOwnerByIndex(walletAddress, i)
                  tokenData.push({
                    contractAddress: collection.contractAddress.toLowerCase(),
                    tokenId: tokenId.toString(),
                  })
                } catch (err) {
                  console.error(`Error fetching token ${i} for collection ${collection.id}:`, err)
                }
              }
            }
          } catch (err) {
            console.error(`Error checking collection ${collection.id} on Base chain:`, err)
          }
        }
      } catch (error) {
        console.error("Error fetching NFTs directly from Base chain:", error)
      }
    }
  } catch (error) {
    console.error("❌ [FALLBACK] Error in Base chain NFT fetching:", error)
    console.log("🔄 [FALLBACK] Trying public Base API endpoint...")

    // Fallback to public Base API endpoint
    try {
      const publicUrl = `https://mainnet.base.org/api/v1/accounts/${walletAddress}?limit=100`
      const publicResponse = await fetch(publicUrl)

      if (publicResponse.ok) {
        const publicData = await publicResponse.json()

        if (publicData.items && publicData.items.length > 0) {
          console.log(`✅ [FALLBACK] Public Base API found ${publicData.items.length} NFTs`)

          publicData.items.forEach((item: any) => {
            if (item.contract_address && item.token_id) {
              tokenData.push({
                contractAddress: item.contract_address.toLowerCase(),
                tokenId: item.token_id,
              })
            }
          })
        }
      }
    } catch (fallbackError) {
      console.error("❌ [FALLBACK] Public Base API also failed:", fallbackError)
    }
  }

  return tokenData
}

// Function to fetch NFTs from Basescan
async function fetchBasescanNFTs(walletAddress: string): Promise<Array<{ contractAddress: string; tokenId: string }>> {
  const tokenData: Array<{ contractAddress: string; tokenId: string }> = []

  try {
    // Fetch ERC-721 tokens (NFTs) from Basescan
    const erc721Url = `${BASESCAN_BASE_URL}?module=account&action=tokennfttx&address=${walletAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${BASESCAN_API_KEY}`
    const erc721Response = await fetch(erc721Url)

    if (!erc721Response.ok) {
      throw new Error(`Basescan API error: ${erc721Response.status}`)
    }

    const erc721Data: EtherscanNFTResponse = await erc721Response.json()

    if (DEBUG_MODE) {
      console.log("Basescan ERC-721 response status:", erc721Data.status)
      console.log("Basescan ERC-721 result count:", erc721Data.result?.length || 0)
    }

    if (erc721Data.status === "1" && Array.isArray(erc721Data.result)) {
      // Process ERC-721 transactions to find current holdings
      // We need to track transfers in and out to determine current holdings
      const holdings = new Map<string, Set<string>>() // contractAddress -> Set of tokenIds

      erc721Data.result.forEach((tx) => {
        if (!tx) return

        const contractAddress = (tx.TokenAddress || tx.contractAddress || "").toLowerCase()
        const tokenId = tx.TokenId || ""

        if (!contractAddress || !tokenId) return

        // If this wallet is the recipient, add the token
        if (tx.to && tx.to.toLowerCase() === walletAddress.toLowerCase()) {
          if (!holdings.has(contractAddress)) {
            holdings.set(contractAddress, new Set())
          }
          holdings.get(contractAddress)?.add(tokenId)
        }

        // If this wallet is the sender, remove the token
        if (tx.from && tx.from.toLowerCase() === walletAddress.toLowerCase()) {
          holdings.get(contractAddress)?.delete(tokenId)
        }
      })

      // Convert the holdings map to our return format
      holdings.forEach((tokenIds, contractAddress) => {
        tokenIds.forEach((tokenId) => {
          tokenData.push({ contractAddress, tokenId })
        })
      })
    }
  } catch (error) {
    console.error("Error fetching ERC-721 tokens from Basescan:", error)
  }

  try {
    // Fetch ERC-1155 tokens (can also be NFTs) from Basescan
    const erc1155Url = `${BASESCAN_BASE_URL}?module=account&action=token1155tx&address=${walletAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${BASESCAN_API_KEY}`
    const erc1155Response = await fetch(erc1155Url)

    if (!erc1155Response.ok) {
      throw new Error(`Basescan API error: ${erc1155Response.status}`)
    }

    const erc1155Data: EtherscanNFTResponse = await erc1155Response.json()

    if (DEBUG_MODE) {
      console.log("Basescan ERC-1155 response status:", erc1155Data.status)
      console.log("Basescan ERC-1155 result count:", erc1155Data.result?.length || 0)
    }

    if (erc1155Data.status === "1" && Array.isArray(erc1155Data.result)) {
      // Similar processing for ERC-1155 tokens
      const holdings = new Map<string, Set<string>>()

      erc1155Data.result.forEach((tx) => {
        if (!tx) return

        const contractAddress = (tx.TokenAddress || tx.contractAddress || "").toLowerCase()
        const tokenId = tx.TokenId || ""

        if (!contractAddress || !tokenId) return

        // If this wallet is the recipient, add the token
        if (tx.to && tx.to.toLowerCase() === walletAddress.toLowerCase()) {
          if (!holdings.has(contractAddress)) {
            holdings.set(contractAddress, new Set())
          }
          holdings.get(contractAddress)?.add(tokenId)
        }

        // If this wallet is the sender, remove the token
        if (tx.from && tx.from.toLowerCase() === walletAddress.toLowerCase()) {
          holdings.get(contractAddress)?.delete(tokenId)
        }
      })

      // Convert the holdings map to our return format
      holdings.forEach((tokenIds, contractAddress) => {
        tokenIds.forEach((tokenId) => {
          tokenData.push({ contractAddress, tokenId })
        })
      })
    }
  } catch (error) {
    console.error("Error fetching ERC-1155 tokens from Basescan:", error)
  }

  return tokenData
}

// Also update the fetchSimulatedCollections function to skip Base chain NFTs
// Function to manually check if a wallet owns NFTs from a specific collection
// This can be used as a fallback or for debugging
/**
 * Manually checks if a wallet owns a specific NFT collection
 *
 * @param walletAddress - The wallet address to check
 * @param collectionId - The ID of the collection to check (e.g., "bored-ape-yacht-club")
 * @returns Promise<boolean> - True if the wallet owns the collection, false otherwise
 *
 * This function performs a direct contract check against the collection's smart contract
 * to determine ownership. It's used as a fallback when API calls fail or for specific
 * collections that require direct blockchain interaction.
 */
export async function manuallyCheckCollection(walletAddress: string, collectionId: string): Promise<boolean> {
  try {
    const collection = COLLECTIONS.find((c) => c.id === collectionId)
    if (!collection) {
      console.error(`Collection ${collectionId} not found in our list`)
      return false
    }

    // Debug logging for Moonbirds specifically
    if (collectionId === "moonbirds") {
      console.log("🔍 [DEBUG] manuallyCheckCollection called for Moonbirds")
    }

    // Note: Removed special handling for Grifters (replaced with Lil Pudgys)

    // Try Alchemy first
    try {
      const url = `${ALCHEMY_BASE_URL}${ALCHEMY_API_KEY}/getNFTs/?owner=${walletAddress}&contractAddresses[]=${collection.contractAddress}`
      const response = await fetch(url)

      if (response.ok) {
        const data = await response.json()
        if (data.totalCount > 0) {
          console.log(`Found ${data.totalCount} NFTs from collection ${collectionId} using Alchemy`)
          return true
        }
      }
    } catch (error) {
      console.error(`Error checking collection ${collectionId} with Alchemy:`, error)
    }

    // Try Etherscan as fallback
    try {
      const erc721Url = `${ETHERSCAN_BASE_URL}?module=account&action=tokennfttx&address=${walletAddress}&contractaddress=${collection.contractAddress}&startblock=0&endblock=999999999&sort=asc&apikey=${ETHERSCAN_API_KEY}`
      const response = await fetch(erc721Url)

      if (response.ok) {
        const data = await response.json()
        if (data.status === "1" && Array.isArray(data.result) && data.result.length > 0) {
          console.log(`Found NFTs from collection ${collectionId} using Etherscan`)
          return true
        }
      }
    } catch (error) {
      console.error(`Error checking collection ${collectionId} with Etherscan:`, error)
    }

    return false
  } catch (error) {
    console.error(`Error in manual collection check:`, error)
    return false
  }
}

// Function to add a missing collection to the tower
export function addMissingCollection(walletAddress: string, contractAddress: string): Promise<NFTCollection[]> {
  return new Promise((resolve) => {
    // Find the collection in our list that matches this contract address
    const collection = COLLECTIONS.find(
      (c) =>
        c.contractAddress.toLowerCase() === contractAddress.toLowerCase() ||
        (c.relatedContracts || []).some((addr) => addr.toLowerCase() === contractAddress.toLowerCase()),
    )

    if (!collection) {
      console.error(`Contract address ${contractAddress} not found in our collections list`)
      // Return the regular collections
      fetchWalletCollections(walletAddress).then(resolve)
      return
    }

    // Fetch the regular collections first
    fetchWalletCollections(walletAddress).then((collections) => {
      // Check if this collection is already included
      if (collections.some((c) => c.id === collection.id)) {
        console.log(`Collection ${collection.id} is already included in the results`)
        resolve(collections)
        return
      }

      // Add this collection to the results if it's not hidden
      if (collection.hidden) {
        console.log(`Collection ${collection.id} is hidden and will not be added to the results`)
        resolve(collections)
        return
      }

      const updatedCollection = {
        ...collection,
        owned: true,
        tokenCount: 1, // Assume at least one token
      }

      const updatedCollections = [...collections, updatedCollection]
      console.log(`Manually added collection ${collection.id} to the results`)

      resolve(updatedCollections)
    })
  })
}

// Function to manually set the token count for a collection
export function setCollectionTokenCount(
  walletAddress: string,
  collectionId: string,
  tokenCount: number,
): Promise<NFTCollection[]> {
  return new Promise((resolve) => {
    // Fetch the current collections
    fetchWalletCollections(walletAddress).then((collections) => {
      // Find the collection to update
      const collectionIndex = collections.findIndex((c) => c.id === collectionId)

      if (collectionIndex === -1) {
        // Collection not found, try to add it
        const collection = COLLECTIONS.find((c) => c.id === collectionId)
        if (!collection) {
          console.error(`Collection ${collectionId} not found in our collections list`)
          resolve(collections)
          return
        }

        // Add the collection with the specified token count
        const updatedCollection = {
          ...collection,
          owned: true,
          tokenCount: tokenCount,
        }

        const updatedCollections = [...collections, updatedCollection]
        console.log(`Manually added collection ${collectionId} with token count ${tokenCount}`)

        resolve(updatedCollections)
        return
      }

      // Update the token count for the existing collection
      const updatedCollections = [...collections]
      updatedCollections[collectionIndex] = {
        ...updatedCollections[collectionIndex],
        tokenCount: tokenCount,
      }

      console.log(`Updated token count for collection ${collectionId} to ${tokenCount}`)
      resolve(updatedCollections)
    })
  })
}

// Update the RPC URL to use a public endpoint
    const RPC_URL = process.env.ETHEREUM_RPC_URL || process.env.NEXT_PUBLIC_ETHEREUM_RPC_URL || "https://eth-mainnet.g.alchemy.com/v2/demo"

// Add better error handling for RPC connection
async function checkContractBalance(
  contractAddress: string,
  walletAddress: string,
  tokenId?: number
): Promise<boolean> {
  try {
    const provider = new ethers.JsonRpcProvider(RPC_URL)
    const contract = new ethers.Contract(
      contractAddress,
      ["function balanceOf(address) view returns (uint256)", "function ownerOf(uint256) view returns (address)"],
      provider
    )

    // Add null check for contract and provider
    if (!contract || !contract.balanceOf) {
      console.warn(`Contract not properly initialized for ${contractAddress}`)
      return false
    }

    if (tokenId !== undefined) {
      // Add null check for ownerOf method
      if (!contract.ownerOf) {
        console.warn(`Contract ownerOf method not available for ${contractAddress}`)
        return false
      }
      const owner = await contract.ownerOf(tokenId)
      return owner.toLowerCase() === walletAddress.toLowerCase()
    } else {
      const balance = await contract.balanceOf(walletAddress)
      return balance > 0
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.error("Error checking contract balance:", error.message)
    } else {
      console.error("Unknown error checking contract balance:", error)
    }
    return false
  }
}

// ============================================================================
// DELEGATE.XYZ API INTEGRATION
// ============================================================================

/**
 * Types and interfaces for Delegate.xyz API v2
 */

// Delegation types according to Delegate API v2
export type DelegationType = "NONE" | "ALL" | "CONTRACT" | "TOKEN";

// Delegation interface matching API v2 response
export interface Delegation {
  type: DelegationType;
  from: string;
  to: string;
  contract: string | null;
  tokenId: number | null;
}

// Wallet with delegation information
export interface DelegateWallet {
  address: string;         // ETH address or ENS domain
  type: "delegated" | "delegator";
  permissions: Delegation[]; // Array of delegations with full info
  chainId?: number;        // Optional chain ID
}

// Delegation check response types
export interface DelegationCheckResponse {
  hasDelegation: boolean;
  delegationType?: DelegationType;
  contract?: string;
  tokenId?: number;
}

/**
 * Interface for wallet search results
 */
interface WalletSearchResult {
  walletAddress: string
  isMainWallet: boolean
  contractCounts: Record<string, number>
  tokenIdsByContract: Record<string, Set<string>>
}

/**
 * Fetches delegated wallets from Delegate.xyz API v2
 *
 * @param walletAddress - The wallet address to check for delegations
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @returns Promise<DelegateWallet[]> - Array of delegated wallets
 *
 * Uses Delegate.xyz API v2 to get incoming and outgoing delegations
 * for the specified wallet address.
 */
export async function fetchDelegatedWallets(
  walletAddress: string,
  chainId: number = 1
): Promise<DelegateWallet[]> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, skipping delegated wallet lookup")
      return []
    }

    const url = `https://api.delegate.xyz/registry/v2/${walletAddress}?chainId=${chainId}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    console.log(`🔍 Fetching delegated wallets for ${walletAddress} on chain ${chainId}`)

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.error(`❌ Delegate.xyz API error: ${response.status} ${response.statusText}`)
      return []
    }

    const data: Delegation[] = await response.json()

    if (!data || !Array.isArray(data)) {
      console.warn("⚠️ Invalid response from Delegate.xyz API")
      return []
    }

    // Group delegations by address and determine wallet type
    const walletMap = new Map<string, { delegations: Delegation[], type: "delegated" | "delegator" }>()

    data.forEach((delegation) => {
      // For incoming delegations (wallet is 'to'), it's a delegated wallet
      if (delegation.to.toLowerCase() === walletAddress.toLowerCase()) {
        const delegator = delegation.from
        if (!walletMap.has(delegator)) {
          walletMap.set(delegator, { delegations: [], type: "delegator" })
        }
        walletMap.get(delegator)!.delegations.push(delegation)
      }

      // For outgoing delegations (wallet is 'from'), it's a delegated wallet
      if (delegation.from.toLowerCase() === walletAddress.toLowerCase()) {
        const delegate = delegation.to
        if (!walletMap.has(delegate)) {
          walletMap.set(delegate, { delegations: [], type: "delegated" })
        }
        walletMap.get(delegate)!.delegations.push(delegation)
      }
    })

    const delegatedWallets: DelegateWallet[] = Array.from(walletMap.entries())
      .map(([address, { delegations, type }]) => ({
        address,
        type,
        permissions: delegations,
        chainId
      }))
      .filter(wallet => wallet.address && wallet.address.toLowerCase() !== walletAddress.toLowerCase())

    console.log(`✅ Found ${delegatedWallets.length} delegated wallets for ${walletAddress}`)
    if (DEBUG_MODE) {
      delegatedWallets.forEach(wallet => {
        console.log(`  - ${wallet.address} (${wallet.type})`)
        wallet.permissions.forEach(perm => {
          console.log(`    → ${perm.type} delegation ${perm.from} → ${perm.to}${perm.contract ? ` for ${perm.contract}` : ''}`)
        })
      })
    }

    return delegatedWallets
  } catch (error) {
    console.error("❌ Error fetching delegated wallets:", error)
    return []
  }
}

/**
 * Fetches incoming delegations for a wallet (where wallet is the 'to' address)
 * This helps determine if a wallet is a delegated wallet
 *
 * @param walletAddress - The wallet address to check for incoming delegations
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @returns Promise<Delegation[]> - Array of incoming delegations
 */
export async function fetchDelegatedWalletsForIncoming(
  walletAddress: string,
  chainId: number = 1
): Promise<Delegation[]> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, skipping incoming delegation lookup")
      return []
    }

    const url = `https://api.delegate.xyz/registry/v2/${walletAddress}?chainId=${chainId}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    console.log(`🔍 Fetching incoming delegations for ${walletAddress} on chain ${chainId}`)

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.error(`❌ Delegate.xyz API error: ${response.status} ${response.statusText}`)
      return []
    }

    const data: Delegation[] = await response.json()

    if (!data || !Array.isArray(data)) {
      console.warn("⚠️ Invalid response from Delegate.xyz API")
      return []
    }

    // Filter for incoming delegations (where wallet is 'to')
    const incomingDelegations = data.filter(delegation =>
      delegation.to.toLowerCase() === walletAddress.toLowerCase()
    )

    console.log(`✅ Found ${incomingDelegations.length} incoming delegations for ${walletAddress}`)

    if (DEBUG_MODE && incomingDelegations.length > 0) {
      incomingDelegations.forEach((delegation, index) => {
        console.log(`  ${index + 1}. From ${delegation.from} → ${delegation.to} (${delegation.type})`)
      })
    }

    return incomingDelegations
  } catch (error) {
    console.error("❌ Error fetching incoming delegations:", error)
    return []
  }
}

/**
 * Check delegation functions for Delegate.xyz API v2
 */

/**
 * Checks if a delegate is granted to act on behalf of the vault for all rights
 *
 * @param delegate - The hot wallet to act on behalf
 * @param vault - The cold wallet who issued the delegation
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @param rights - Optional bytes32 of rights string
 * @returns Promise<boolean> - True if delegation exists
 */
export async function checkDelegationAll(
  delegate: string,
  vault: string,
  chainId: number = 1,
  rights?: string
): Promise<boolean> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, delegation check unavailable")
      return false
    }

    const params = new URLSearchParams({
      to: delegate,
      from: vault,
      chainId: chainId.toString()
    })

    if (rights) {
      params.append('rights', rights)
    }

    const url = `https://api.delegate.xyz/registry/v2/check/all?${params}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.warn(`⚠️ Delegation check failed: ${response.status} ${response.statusText}`)
      return false
    }

    return await response.json()
  } catch (error) {
    console.error("❌ Error checking delegation (all):", error)
    return false
  }
}

/**
 * Checks if a delegate is granted to act on behalf of the vault for a specific contract
 *
 * @param delegate - The hot wallet to act on behalf
 * @param vault - The cold wallet who issued the delegation
 * @param contract - The address for the contract being delegated
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @param rights - Optional bytes32 of rights string
 * @returns Promise<boolean> - True if delegation exists
 */
export async function checkDelegationContract(
  delegate: string,
  vault: string,
  contract: string,
  chainId: number = 1,
  rights?: string
): Promise<boolean> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, delegation check unavailable")
      return false
    }

    const params = new URLSearchParams({
      to: delegate,
      from: vault,
      contract,
      chainId: chainId.toString()
    })

    if (rights) {
      params.append('rights', rights)
    }

    const url = `https://api.delegate.xyz/registry/v2/check/contract?${params}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.warn(`⚠️ Delegation check failed: ${response.status} ${response.statusText}`)
      return false
    }

    return await response.json()
  } catch (error) {
    console.error("❌ Error checking delegation (contract):", error)
    return false
  }
}

/**
 * Checks if a delegate is granted to act on behalf of the vault for a specific ERC721 token
 *
 * @param delegate - The hot wallet to act on behalf
 * @param vault - The cold wallet who issued the delegation
 * @param contract - The address for the contract being delegated
 * @param tokenId - The token ID for the token being delegated
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @param rights - Optional bytes32 of rights string
 * @returns Promise<boolean> - True if delegation exists
 */
export async function checkDelegationERC721(
  delegate: string,
  vault: string,
  contract: string,
  tokenId: number,
  chainId: number = 1,
  rights?: string
): Promise<boolean> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, delegation check unavailable")
      return false
    }

    const params = new URLSearchParams({
      delegate,
      vault,
      contract,
      tokenId: tokenId.toString(),
      chainId: chainId.toString()
    })

    if (rights) {
      params.append('rights', rights)
    }

    const url = `https://api.delegate.xyz/registry/v2/check/erc721?${params}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.warn(`⚠️ Delegation check failed: ${response.status} ${response.statusText}`)
      return false
    }

    return await response.json()
  } catch (error) {
    console.error("❌ Error checking delegation (ERC721):", error)
    return false
  }
}

/**
 * Checks if a delegate is granted to act on behalf of the vault for a specific ERC1155 token
 *
 * @param delegate - The hot wallet to act on behalf
 * @param vault - The cold wallet who issued the delegation
 * @param contract - The address for the contract being delegated
 * @param tokenId - The token ID for the token being delegated
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @param rights - Optional bytes32 of rights string
 * @returns Promise<boolean> - True if delegation exists
 */
export async function checkDelegationERC1155(
  delegate: string,
  vault: string,
  contract: string,
  tokenId: number,
  chainId: number = 1,
  rights?: string
): Promise<boolean> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, delegation check unavailable")
      return false
    }

    const params = new URLSearchParams({
      delegate,
      vault,
      contract,
      tokenId: tokenId.toString(),
      chainId: chainId.toString()
    })

    if (rights) {
      params.append('rights', rights)
    }

    const url = `https://api.delegate.xyz/registry/v2/check/erc1155?${params}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.warn(`⚠️ Delegation check failed: ${response.status} ${response.statusText}`)
      return false
    }

    return await response.json()
  } catch (error) {
    console.error("❌ Error checking delegation (ERC1155):", error)
    return false
  }
}

/**
 * Checks if a delegate is granted to act on behalf of the vault for a specific ERC20 token
 *
 * @param delegate - The hot wallet to act on behalf
 * @param vault - The cold wallet who issued the delegation
 * @param contract - The address for the contract being delegated
 * @param chainId - Optional chain ID (defaults to 1 for mainnet)
 * @param rights - Optional bytes32 of rights string
 * @returns Promise<boolean> - True if delegation exists
 */
export async function checkDelegationERC20(
  delegate: string,
  vault: string,
  contract: string,
  chainId: number = 1,
  rights?: string
): Promise<boolean> {
  try {
    const apiKey = process.env.DELEGATE_API_KEY || process.env.NEXT_PUBLIC_DELEGATE_API_KEY

    if (!apiKey) {
      console.warn("⚠️ DELEGATE_API_KEY not found, delegation check unavailable")
      return false
    }

    const params = new URLSearchParams({
      delegate,
      vault,
      contract,
      chainId: chainId.toString()
    })

    if (rights) {
      params.append('rights', rights)
    }

    const url = `https://api.delegate.xyz/registry/v2/check/erc20?${params}`
    const headers = {
      "X-API-KEY": apiKey,
      "Content-Type": "application/json"
    }

    const response = await fetch(url, {
      method: "GET",
      headers: headers
    })

    if (!response.ok) {
      console.warn(`⚠️ Delegation check failed: ${response.status} ${response.statusText}`)
      return false
    }

    return await response.json()
  } catch (error) {
    console.error("❌ Error checking delegation (ERC20):", error)
    return false
  }
}

/**
 * Resolves ENS domain to Ethereum address
 *
 * @param ensDomain - The ENS domain to resolve
 * @returns Promise<string | null> - Resolved ETH address or null if not found
 */
export async function resolveEnsToAddress(ensDomain: string): Promise<string | null> {
  try {
    if (!ensDomain.includes(".eth")) {
      // Not an ENS domain, return as-is
      return ensDomain
    }

    const provider = new ethers.JsonRpcProvider(
      process.env.ETHEREUM_RPC_URL ||
      process.env.NEXT_PUBLIC_ETHEREUM_RPC_URL ||
      "https://eth-mainnet.g.alchemy.com/v2/demo"
    )

    const resolvedAddress = await provider.resolveName(ensDomain)

    if (resolvedAddress) {
      console.log(`✅ Resolved ENS ${ensDomain} to ${resolvedAddress}`)
      return resolvedAddress
    } else {
      console.warn(`⚠️ Could not resolve ENS domain: ${ensDomain}`)
      return null
    }
  } catch (error) {
    console.error(`❌ Error resolving ENS domain ${ensDomain}:`, error)
    return null
  }
}

/**
 * Merges multiple wallet search results into a single result
 *
 * @param walletResults - Array of wallet search results
 * @returns WalletSearchResult - Merged result with combined contract counts
 */
export function mergeWalletResults(walletResults: WalletSearchResult[]): WalletSearchResult {
  const merged: WalletSearchResult = {
    walletAddress: "merged",
    isMainWallet: false,
    contractCounts: {},
    tokenIdsByContract: {}
  }

  console.log(`🔄 Merging ${walletResults.length} wallet results:`);

  walletResults.forEach((result, index) => {
    console.log(`  Wallet ${index + 1}: ${result.walletAddress} - ${Object.keys(result.contractCounts).length} contracts`);

    // Merge contract counts
    Object.entries(result.contractCounts).forEach(([contractAddress, count]) => {
      merged.contractCounts[contractAddress] = (merged.contractCounts[contractAddress] || 0) + count
    })

    // Merge token IDs by contract
    Object.entries(result.tokenIdsByContract).forEach(([contractAddress, tokenIds]) => {
      if (!merged.tokenIdsByContract[contractAddress]) {
        merged.tokenIdsByContract[contractAddress] = new Set()
      }
      tokenIds.forEach(tokenId => {
        merged.tokenIdsByContract[contractAddress].add(tokenId)
      });
    })
  });

  console.log(`✅ Merged result: ${Object.keys(merged.contractCounts).length} total contracts`);

  console.log(`✅ Merged ${walletResults.length} wallet results`)
  console.log(`📊 Total unique contracts: ${Object.keys(merged.contractCounts).length}`)

  return merged
}

// Fix popularity undefined error
const sortedCollections = COLLECTIONS
  .sort((a, b) => {
    const aPopularity = a.popularity ?? 0
    const bPopularity = b.popularity ?? 0
    return bPopularity - aPopularity
  })
  .slice(0, 10)
